import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Send,
  Bot,
  User,
  Square,
  SquarePen,
  Info,
  Menu,
  Settings,
  Sparkles,
  ListChecks,
  MessageSquare,
  Check,
  Loader2,
  Pencil,
  RotateCcw,
  X,
  PanelLeft,
  ChevronsLeft,
  Paperclip,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { FavoriteButton } from '../../components/FavoriteButton';
import { ArcheExpandButton } from '../../components/ArcheExpandButton';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ArcheLayout } from '../../components/ArcheLayout';
import { useSidebar } from '../../context/SidebarContext';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetBody,
} from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { EntityParameterPanel } from '@/components/EntityParameterPanel';
import { authApi } from '@/lib/auth-api';
import { api, ModelProvider, ModelTier, ConversationMessageInput } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useMultiStreaming, MultiAIProvider } from '@/lib/hooks/useMultiStreaming';
import { useAutoScroll } from '@/lib/hooks/useAutoScroll';
import { useModels } from '@/lib/hooks/useModels';
import { useModelDefinition } from '@/lib/hooks/useModelDefinition';
import { useTextHighlightLearnMore } from '@/lib/hooks/useTextHighlightLearnMore';
import { useFileContext } from '@/lib/hooks/useFileContext';
import { LearnMorePopup } from '@/components/LearnMorePopup';
import { FileContextDropZone } from '@/components/FileContextDropZone';
import { AttachedFiles } from '@/components/AttachedFiles';
import { VaultFilePickerDialog } from '@/components/VaultFilePickerDialog';
import {
  extractTextFromVaultDocument,
  isVaultDocumentExtractable,
  VaultDocumentExtractionError,
} from '@/lib/extractTextFromVaultDocument';
import { MAX_FILE_SIZE, formatBytes } from '@/lib/hooks/useFileContext';
import { traceStart, traceEnd, traceError, addBreadcrumb, logDebug } from '@/lib/telemetry';
import { formatMessageContent } from '@/lib/format-error';
import {
  InsufficientCreditsMessage,
  ModelCostLabel,
  InteractionCostLabel,
} from '@/credits/components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ConversationSidebar } from '@/components/ConversationSidebar';
import { ShareDialog } from '@/components/ShareDialog';
import { toast } from '@/components/ui/use-toast';
import { useConversation } from '@/lib/hooks/useConversation';
import { useArcheSession } from '@/lib/hooks/useArcheSession';
import { WEBSOCKET_ENDPOINT } from '@/lib/api';
import { shareOrigin } from '@/lib/portal-host';

// Arche ID for multi-ai
const ARCHE_ID = 'arche_multi_ai';

const MAX_CONVERSATION_MESSAGES = 20;

type ActiveTab = 'responses' | 'summary' | 'assumptions';

// GPT-5 reasoning effort levels
type ReasoningEffort = 'low' | 'medium' | 'high' | 'xhigh';
type Verbosity = 'low' | 'medium' | 'high';

// Gemini thinking levels
type ThinkingLevel = 'LOW' | 'HIGH';

// Default model parameters for GPT-4.x
const DEFAULT_GPT4_PARAMS = {
  maxOutputTokens: 1000,
  temperature: 0.7,
};

// Default model parameters for GPT-5
const DEFAULT_GPT5_PARAMS = {
  reasoningEffort: 'medium' as ReasoningEffort,
  verbosity: 'medium' as Verbosity,
  maxOutputTokens: 8000,
  reasoning: true, // Default on, but will be set based on model
};

// Helper to determine default reasoning state based on GPT-5 model
const getDefaultGpt5Reasoning = (modelName: string): boolean => {
  const lowerName = modelName.toLowerCase();
  // GPT5-mini and GPT5-nano default to reasoning OFF
  if (lowerName.includes('mini') || lowerName.includes('nano')) {
    return false;
  }
  // GPT5.2 and other GPT-5 models default to reasoning ON
  return true;
};

// Default model parameters for Claude
const DEFAULT_CLAUDE_PARAMS = {
  maxTokens: 16000,
  budgetTokens: 10000,
  temperature: 1,
};

// Default model parameters for Gemini
const DEFAULT_GEMINI_PARAMS = {
  maxOutputTokens: 8000,
  temperature: 0.7,
  thinkingLevel: 'HIGH' as ThinkingLevel,
};

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  /** Full content including file context (for conversation history) */
  contextContent?: string;
  thinking?: string;
  isStreaming?: boolean;
  /** Credits this column's response actually charged (from node_complete); 1 credit = 1¢. */
  costCredits?: number;
}

interface ConsolidatedAssumption {
  assumption: string;
  col0: boolean;
  col1: boolean;
  col2: boolean;
}

interface ColumnConfig {
  provider: ModelProvider;
  modelEntityId: string;
  // Per-column thinking toggle
  showThinking: boolean;
  // GPT-4.x parameters
  gpt4MaxOutputTokens: number;
  gpt4Temperature: number;
  // GPT-5 parameters
  gpt5Reasoning: boolean;
  reasoningEffort: ReasoningEffort;
  verbosity: Verbosity;
  gptMaxOutputTokens: number;
  // Claude parameters
  claudeMaxTokens: number;
  claudeBudgetTokens: number;
  claudeTemperature: number;
  // Gemini parameters
  geminiMaxOutputTokens: number;
  geminiTemperature: number;
  geminiThinkingLevel: ThinkingLevel;
}

// Create default column config
const createDefaultColumnConfig = (provider: ModelProvider): ColumnConfig => ({
  provider,
  modelEntityId: '',
  showThinking: false,
  gpt4MaxOutputTokens: DEFAULT_GPT4_PARAMS.maxOutputTokens,
  gpt4Temperature: DEFAULT_GPT4_PARAMS.temperature,
  gpt5Reasoning: DEFAULT_GPT5_PARAMS.reasoning,
  reasoningEffort: DEFAULT_GPT5_PARAMS.reasoningEffort,
  verbosity: DEFAULT_GPT5_PARAMS.verbosity,
  gptMaxOutputTokens: DEFAULT_GPT5_PARAMS.maxOutputTokens,
  claudeMaxTokens: DEFAULT_CLAUDE_PARAMS.maxTokens,
  claudeBudgetTokens: DEFAULT_CLAUDE_PARAMS.budgetTokens,
  claudeTemperature: DEFAULT_CLAUDE_PARAMS.temperature,
  geminiMaxOutputTokens: DEFAULT_GEMINI_PARAMS.maxOutputTokens,
  geminiTemperature: DEFAULT_GEMINI_PARAMS.temperature,
  geminiThinkingLevel: DEFAULT_GEMINI_PARAMS.thinkingLevel,
});

// Map column index to the streaming provider key (used by useMultiStreaming hook)
const COLUMN_TO_STREAM_KEY: MultiAIProvider[] = ['openai', 'anthropic', 'google'];

// Provider colors for visual distinction
const PROVIDER_COLORS: Record<ModelProvider, string> = {
  openai: 'text-green-600 dark:text-green-400',
  anthropic: 'text-orange-600 dark:text-orange-400',
  google: 'text-blue-600 dark:text-blue-400',
  xai: 'text-purple-600 dark:text-purple-400',
};

const TIER_LABELS: Record<ModelTier, string> = {
  fast: 'Fast',
  balanced: 'Balanced',
  powerful: 'Powerful',
};

/**
 * ColumnParameterPanel - Renders model-definition-driven parameters for a column
 *
 * This component wraps the useModelDefinition hook and EntityParameterPanel
 * so it can be used inside the column map (hooks can't be called in loops).
 * Changes are persisted to columnConfigs via the onChange callback.
 */
interface ColumnParameterPanelProps {
  provider: ModelProvider;
  modelName: string | undefined;
  modelDisplayName: string | undefined;
  modelEntityId: string | undefined;
  disabled: boolean;
  config: ColumnConfig;
  onConfigChange: (updates: Partial<ColumnConfig>) => void;
  // Per-model parameter storage
  allModelParams: Record<string, Record<string, unknown>>;
  onModelParamsChange: (modelId: string, params: Record<string, unknown>) => void;
}

// Map schema parameter names to ColumnConfig field names
const SCHEMA_TO_CONFIG_MAP: Record<string, keyof ColumnConfig> = {
  // OpenAI
  maxOutputTokens: 'gptMaxOutputTokens',
  reasoningEnabled: 'gpt5Reasoning',
  reasoningEffort: 'reasoningEffort',
  verbosity: 'verbosity',
  showThinking: 'showThinking',
  // Claude
  maxTokens: 'claudeMaxTokens',
  budgetTokens: 'claudeBudgetTokens',
  temperature: 'claudeTemperature',
  thinkingEnabled: 'showThinking', // Maps to showThinking for Claude/Gemini
  // Gemini
  thinkingLevel: 'geminiThinkingLevel',
  // geminiMaxOutputTokens and geminiTemperature use same schema names
};

function ColumnParameterPanel({
  provider,
  modelName,
  modelDisplayName,
  modelEntityId,
  disabled,
  config,
  onConfigChange,
  allModelParams,
  onModelParamsChange,
}: ColumnParameterPanelProps) {
  const { parameterSchema, loading: modelDefLoading } = useModelDefinition({
    provider,
    modelName,
  });

  // Check if this is a GPT-4.x, GPT-5, or Grok model (different param handling)
  const isGpt4Model = modelName?.startsWith('gpt-4') ?? false;
  const isGpt5Model = modelName?.startsWith('gpt-5') ?? false;
  const isGrokModel = modelName?.includes('grok') ?? false;

  // Get stored parameters for this specific model (if any)
  const storedModelParams = modelEntityId ? allModelParams[modelEntityId] : undefined;

  // Build parameter values - prefer per-model storage, fall back to config
  const parameterValues = useMemo(() => {
    const values: Record<string, unknown> = {};
    for (const param of parameterSchema) {
      // First check if we have a stored value for this model
      if (storedModelParams && param.name in storedModelParams) {
        values[param.name] = storedModelParams[param.name];
        continue;
      }
      // Fall back to config values (for backwards compatibility)
      // Handle provider-specific mappings FIRST (before generic mapping)
      if (param.name === 'maxOutputTokens' && provider === 'google') {
        values[param.name] = config.geminiMaxOutputTokens;
      } else if (param.name === 'temperature' && provider === 'google') {
        values[param.name] = config.geminiTemperature;
      } else if (param.name === 'maxTokens' && provider === 'openai' && isGpt4Model) {
        // GPT-4.x uses maxTokens schema param
        values[param.name] = config.gpt4MaxOutputTokens;
      } else if (param.name === 'temperature' && provider === 'openai' && isGpt4Model) {
        // GPT-4.x has temperature
        values[param.name] = config.gpt4Temperature;
      } else {
        // Generic mapping for other params
        const configKey = SCHEMA_TO_CONFIG_MAP[param.name];
        if (configKey && configKey in config) {
          values[param.name] = config[configKey];
        }
      }
    }
    return values;
  }, [parameterSchema, config, provider, isGpt4Model, storedModelParams]);

  // Handle parameter changes and map back to config
  const handleParameterChange = useCallback(
    (newValues: Record<string, unknown>) => {
      const updates: Partial<ColumnConfig> = {};

      for (const [schemaKey, value] of Object.entries(newValues)) {
        // Handle provider-specific mappings
        if (schemaKey === 'maxOutputTokens') {
          if (provider === 'openai' || provider === 'xai') {
            updates.gptMaxOutputTokens = value as number;
          } else if (provider === 'google') {
            updates.geminiMaxOutputTokens = value as number;
          }
        } else if (schemaKey === 'maxTokens' && provider === 'openai' && isGpt4Model) {
          // GPT-4.x uses maxTokens schema param
          updates.gpt4MaxOutputTokens = value as number;
        } else if (schemaKey === 'temperature') {
          if (provider === 'anthropic') {
            updates.claudeTemperature = value as number;
          } else if (provider === 'google') {
            updates.geminiTemperature = value as number;
          } else if (provider === 'openai' && isGpt4Model) {
            // GPT-4.x has temperature
            updates.gpt4Temperature = value as number;
          }
        } else if (schemaKey === 'thinkingEnabled') {
          // Sync to showThinking for Claude/Gemini
          updates.showThinking = value as boolean;
          // When thinking is enabled for Claude, ensure temperature is 1
          if (provider === 'anthropic' && value === true) {
            updates.claudeTemperature = 1;
          }
        } else {
          const configKey = SCHEMA_TO_CONFIG_MAP[schemaKey];
          if (configKey) {
            (updates as Record<string, unknown>)[configKey] = value;
          }
        }
      }

      // Claude constraint: budget_tokens must be less than max_tokens
      if (provider === 'anthropic') {
        const maxTokens = updates.claudeMaxTokens ?? config.claudeMaxTokens;
        let budgetTokens = updates.claudeBudgetTokens ?? config.claudeBudgetTokens;
        if (budgetTokens >= maxTokens) {
          budgetTokens = maxTokens;
          updates.claudeBudgetTokens = budgetTokens;
        }
      }

      onConfigChange(updates);

      // Also store in per-model storage for model-specific persistence
      if (modelEntityId) {
        onModelParamsChange(modelEntityId, newValues);
      }
    },
    [provider, config, onConfigChange, isGpt4Model, modelEntityId, onModelParamsChange],
  );

  if (parameterSchema.length === 0 || modelDefLoading) {
    return null;
  }

  return (
    <div className="border-t-4 border-white pt-4 mt-4">
      <EntityParameterPanel
        parameterSchema={parameterSchema}
        values={parameterValues}
        onChange={handleParameterChange}
        disabled={disabled}
        title={`${modelDisplayName || 'Model'} Parameters`}
        showReset={true}
        priorityParams={
          isGpt5Model || isGrokModel ? ['reasoningEnabled', 'showThinking'] : undefined
        }
      />
    </div>
  );
}

function MultiAIContent() {
  // Fetch available models from the API
  const {
    models,
    providers,
    modelsByProvider,
    modelsByTier,
    getRecommendedModel,
    getModelById,
    loading: modelsLoading,
    error: _modelsError,
    refresh: _refreshModels,
  } = useModels();

  // Helper functions using hook data
  const getProviderColor = (provider: ModelProvider): string => {
    return PROVIDER_COLORS[provider] || '';
  };

  const getProviderLabel = (provider: ModelProvider): string => {
    const providerInfo = providers.find((p) => p.key === provider);
    return providerInfo?.displayName || provider;
  };

  // Column configurations (provider + model + parameters for each of 3 columns)
  const [columnConfigs, setColumnConfigs] = useState<ColumnConfig[]>([
    createDefaultColumnConfig('openai'),
    createDefaultColumnConfig('anthropic'),
    createDefaultColumnConfig('google'),
  ]);
  // Per-column per-model parameter storage
  // Indexed by column (0-2), then by modelEntityId
  const [allColumnModelParams, setAllColumnModelParams] = useState<
    [
      Record<string, Record<string, unknown>>,
      Record<string, Record<string, unknown>>,
      Record<string, Record<string, unknown>>,
    ]
  >([{}, {}, {}]);
  const [selectedTier, setSelectedTier] = useState<ModelTier | null>('fast');
  const [includeContext, setIncludeContext] = useState(true);
  const [configsInitialized, setConfigsInitialized] = useState(false);
  const [enabledPanels, setEnabledPanels] = useState<boolean[]>([true, true, true]); // Track which panels are active

  // Separate conversation histories per column (indexed 0, 1, 2)
  const [conversations, setConversations] = useState<Message[][]>([[], [], []]);

  // Input and streaming state
  const [inputValue, setInputValue] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
  const [currentAssistantIds, setCurrentAssistantIds] = useState<string[] | null>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState<ActiveTab>('responses');

  // Action results
  const [summaryResult, setSummaryResult] = useState<string | null>(null);
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [summaryMessageCount, setSummaryMessageCount] = useState<number>(0); // Track message count when summary was generated
  const [summaryConversation, setSummaryConversation] = useState<Message[]>([]); // Follow-up questions about the summary
  const [summaryInputValue, setSummaryInputValue] = useState('');
  const [isSummaryStreaming, setIsSummaryStreaming] = useState(false);
  // Summary provider/model selection
  const [summaryProvider, setSummaryProvider] = useState<ModelProvider>('anthropic');
  const [summaryModelId, setSummaryModelId] = useState<string | null>(null);

  // Initialize summary model when models are loaded or provider changes
  useEffect(() => {
    if (models.length > 0) {
      const providerModels = modelsByProvider[summaryProvider] || [];
      // For Anthropic, prefer Haiku for speed; otherwise use recommended
      if (summaryProvider === 'anthropic') {
        const haiku = providerModels.find((m) => m.modelName?.toLowerCase().includes('haiku'));
        if (haiku) {
          setSummaryModelId(haiku.entityId);
          return;
        }
      }
      const recommended = getRecommendedModel(summaryProvider);
      if (recommended) {
        setSummaryModelId(recommended.entityId);
      } else if (providerModels.length > 0) {
        setSummaryModelId(providerModels[0].entityId);
      }
    }
  }, [models, summaryProvider, getRecommendedModel, modelsByProvider]);

  // Consolidation provider/model selection (for assumptions tab)
  const [consolidationProvider, setConsolidationProvider] = useState<ModelProvider>('anthropic');
  const [consolidationModelId, setConsolidationModelId] = useState<string | null>(null);

  // Initialize consolidation model when models are loaded or provider changes
  useEffect(() => {
    if (models.length > 0) {
      const providerModels = modelsByProvider[consolidationProvider] || [];
      // For Anthropic, prefer Haiku for speed; otherwise use recommended
      if (consolidationProvider === 'anthropic') {
        const haiku = providerModels.find((m) => m.modelName?.toLowerCase().includes('haiku'));
        if (haiku) {
          setConsolidationModelId(haiku.entityId);
          return;
        }
      }
      const recommended = getRecommendedModel(consolidationProvider);
      if (recommended) {
        setConsolidationModelId(recommended.entityId);
      } else if (providerModels.length > 0) {
        setConsolidationModelId(providerModels[0].entityId);
      }
    }
  }, [models, consolidationProvider, getRecommendedModel, modelsByProvider]);

  const [assumptionsResult, setAssumptionsResult] = useState<string[] | null>(null);
  const [isCheckingAssumptions, setIsCheckingAssumptions] = useState(false);
  const [consolidatedAssumptions, setConsolidatedAssumptions] = useState<
    ConsolidatedAssumption[] | null
  >(null);
  const [isConsolidating, setIsConsolidating] = useState(false);
  const [consolidationStreamText, setConsolidationStreamText] = useState('');
  const [assumptionClarifications, setAssumptionClarifications] = useState<Record<number, string>>(
    {},
  );
  const [editingAssumptionIndex, setEditingAssumptionIndex] = useState<number | null>(null);
  const [isResendingWithClarifications, setIsResendingWithClarifications] = useState(false);

  // Share dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareExpiresAt, setShareExpiresAt] = useState<string | null>(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  // File picker dialog state
  const [filePickerOpen, setFilePickerOpen] = useState(false);

  // File context for drag-and-drop attachments
  const {
    files: attachedFiles,
    totalSize: attachedFilesSize,
    isProcessing: isProcessingFiles,
    addFiles: addAttachedFiles,
    addFileWithContent,
    removeFile: removeAttachedFile,
    clearFiles: clearAttachedFiles,
    formatContext: formatFileContext,
  } = useFileContext({
    onError: (error) => {
      toast({
        title: 'File attachment error',
        description: `${error.fileName}: ${error.error}`,
        variant: 'destructive',
      });
    },
  });

  // Handle files dropped: attach to AI context locally + background-upload
  // to the user's Vault Documents so the dropped file is persisted.
  const handleFilesDropped = useCallback(
    async (files: FileList | File[]) => {
      await addAttachedFiles(files);
      const fileArray = Array.from(files);
      if (fileArray.length === 0) return;
      try {
        const memberships = await api.listMyVaultMemberships();
        if (!memberships.personalHolder) return;
        const holderId = memberships.personalHolder.holderId;
        for (const f of fileArray) {
          api
            .uploadFileToVault(f, holderId, { tags: ['ai-context'], purpose: 'ai-context' })
            .catch((err: unknown) => {
              logDebug('context-upload', 'Background upload failed', {
                error: err instanceof Error ? err.message : String(err),
              });
            });
        }
      } catch (err) {
        logDebug('context-upload', 'Failed to resolve holder for upload', {
          error: err instanceof Error ? err.message : String(err),
        });
      }
    },
    [addAttachedFiles],
  );

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const summaryTextareaRef = useRef<HTMLTextAreaElement>(null);
  const panelsContainerRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const scrollTrackRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState({ scrollLeft: 0, scrollWidth: 0, clientWidth: 0 });
  // Assumptions tab scroll state
  const assumptionsPanelsRef = useRef<HTMLDivElement>(null);
  const assumptionsScrollTrackRef = useRef<HTMLDivElement>(null);
  const [assumptionsScrollState, setAssumptionsScrollState] = useState({
    scrollLeft: 0,
    scrollWidth: 0,
    clientWidth: 0,
  });
  const summaryCleanupRef = useRef<(() => void) | null>(null);
  const summaryConvoCleanupRef = useRef<(() => void) | null>(null);
  const assumptionsCleanupRef = useRef<(() => void) | null>(null);
  const consolidationCleanupRef = useRef<(() => void) | null>(null);
  const resendCleanupRef = useRef<(() => void) | null>(null);
  const { isCollapsed, isMobile, toggle } = useSidebar();
  const authToken = authApi.getIdToken();

  // Track if session has been initialized
  const [sessionInitialized, setSessionInitialized] = useState(false);

  // Conversation persistence
  const {
    conversation,
    conversations: conversationList,
    loading: conversationsLoading,
    createConversation,
    loadConversation,
    addMessages,
    updateTitle,
    archiveConversation,
    deleteConversationById,
    clearConversation,
    refreshList,
  } = useConversation({
    archeId: ARCHE_ID,
  });

  // Session persistence (preferences)
  const {
    session,
    loading: sessionLoading,
    updateMultiAIPreferences,
  } = useArcheSession({
    archeId: ARCHE_ID,
  });

  // Track pending user message for saving after streaming completes
  const pendingUserMessageRef = useRef<{ content: string } | null>(null);

  // Track if we just created a conversation (to avoid overwriting messages)
  const justCreatedConversationRef = useRef(false);
  // Track the last loaded conversation ID to avoid re-restoring when messages are added
  const lastLoadedConversationIdRef = useRef<string | null>(null);

  // Auto-scroll refs for each column
  const scrollRefs = [useAutoScroll(), useAutoScroll(), useAutoScroll()];

  // Auto-scroll ref for summary tab
  const {
    containerRef: summaryContainerRef,
    anchorRef: summaryAnchorRef,
    scrollToBottomIfNeeded: summaryScrollToBottomIfNeeded,
  } = useAutoScroll();

  // Multi-streaming hook - keep enabled for pre-connection, only subscribe when requestId is set
  const {
    isConnected,
    providers: streamingProviders,
    isAllComplete,
    error: streamingError,
    reset: resetStreaming,
    unsubscribe: unsubscribeStreaming,
    connect: connectWebSocket,
    subscribeOneOff,
  } = useMultiStreaming({
    requestId: currentRequestId,
    authToken,
    endpoint: WEBSOCKET_ENDPOINT,
    enabled: true, // Always enabled so WebSocket connects early
  });

  // Initialize column configs once models + session are ready.
  //
  // Resolves each column's modelEntityId with full validation against loaded
  // models, so new users (who arrive with an empty server-created session) and
  // returning users whose saved IDs no longer exist both end up with a working
  // selection. Healed values are persisted back so the next load is fast.
  useEffect(() => {
    if (configsInitialized || sessionLoading || models.length === 0) return;

    const sessionPrefs = session?.type === 'multi-ai' ? session.preferences : null;
    const savedConfigs =
      sessionPrefs?.columnConfigs && sessionPrefs.columnConfigs.length === 3
        ? sessionPrefs.columnConfigs
        : null;

    // Tier: saved value wins; default to 'fast' for a brand-new session.
    // null is a valid saved value meaning "Custom" — respect it only if the
    // user also has saved columnConfigs, otherwise fall back to 'fast'.
    let tier: ModelTier | null;
    if (sessionPrefs?.selectedTier !== undefined) {
      tier = sessionPrefs.selectedTier;
    } else if (savedConfigs) {
      tier = null;
    } else {
      tier = 'fast';
    }
    const tierModels = tier ? modelsByTier[tier] || [] : [];

    const defaultProviders: ModelProvider[] = ['openai', 'anthropic', 'google'];
    const resolveModelId = (provider: ModelProvider, preferredId?: string): string => {
      if (preferredId && models.some((m) => m.entityId === preferredId)) {
        return preferredId;
      }
      // Fall back to the tier's model for this provider, then the provider's
      // recommended model, then the first available model for the provider.
      return (
        tierModels.find((m) => m.provider === provider)?.entityId ||
        getRecommendedModel(provider)?.entityId ||
        modelsByProvider[provider]?.[0]?.entityId ||
        ''
      );
    };

    const resolvedConfigs: ColumnConfig[] = [0, 1, 2].map((idx) => {
      const saved = savedConfigs?.[idx] ?? null;
      const provider = saved?.provider || defaultProviders[idx];
      const modelEntityId = resolveModelId(provider, saved?.modelEntityId);

      const base = createDefaultColumnConfig(provider);
      const modelName = models.find((m) => m.entityId === modelEntityId)?.modelName || '';
      const isGpt5Model = modelName.startsWith('gpt-5');
      const gpt5Reasoning =
        saved?.gpt5Reasoning !== undefined
          ? saved.gpt5Reasoning
          : isGpt5Model
            ? getDefaultGpt5Reasoning(modelName)
            : base.gpt5Reasoning;

      return {
        ...base,
        provider,
        modelEntityId,
        showThinking: saved?.showThinking ?? base.showThinking,
        gpt5Reasoning,
        ...(saved?.providerSettings || {}),
      };
    });

    setColumnConfigs(resolvedConfigs);
    setSelectedTier(tier);
    if (sessionPrefs) {
      setIncludeContext(sessionPrefs.includeContext ?? true);
      if (sessionPrefs.enabledPanels && sessionPrefs.enabledPanels.length === 3) {
        setEnabledPanels(sessionPrefs.enabledPanels);
      }
    }

    // Persist the resolved state if any modelEntityId was healed (or if there
    // were no saved configs at all). This keeps DynamoDB in sync so the next
    // load comes back with valid IDs already in place.
    const needsPersist =
      !savedConfigs ||
      resolvedConfigs.some((c, idx) => c.modelEntityId !== savedConfigs[idx]?.modelEntityId);
    if (needsPersist) {
      updateMultiAIPreferences({
        selectedTier: tier,
        columnConfigs: resolvedConfigs.map((c) => ({
          provider: c.provider,
          modelEntityId: c.modelEntityId,
          showThinking: c.showThinking,
          gpt5Reasoning: c.gpt5Reasoning,
          providerSettings: {
            gpt4MaxOutputTokens: c.gpt4MaxOutputTokens,
            gpt4Temperature: c.gpt4Temperature,
            reasoningEffort: c.reasoningEffort,
            verbosity: c.verbosity,
            gptMaxOutputTokens: c.gptMaxOutputTokens,
            claudeMaxTokens: c.claudeMaxTokens,
            claudeBudgetTokens: c.claudeBudgetTokens,
            claudeTemperature: c.claudeTemperature,
            geminiMaxOutputTokens: c.geminiMaxOutputTokens,
            geminiTemperature: c.geminiTemperature,
            geminiThinkingLevel: c.geminiThinkingLevel,
          },
        })),
      });
    }

    setConfigsInitialized(true);

    if (sessionPrefs?.activeConversationId) {
      loadConversation(sessionPrefs.activeConversationId).then(() => {
        setSessionInitialized(true);
      });
    } else {
      setSessionInitialized(true);
    }
  }, [
    configsInitialized,
    sessionLoading,
    models,
    modelsByTier,
    modelsByProvider,
    session,
    getRecommendedModel,
    loadConversation,
    updateMultiAIPreferences,
  ]);

  // Restore messages from loaded conversation
  // Only restore when explicitly loading an existing conversation, not when creating a new one
  useEffect(() => {
    const currentConvId = conversation?.conversationId || null;

    // Skip if we just created this conversation (messages are already in local state)
    // But still update the ref so we don't re-restore on subsequent updates
    if (justCreatedConversationRef.current) {
      justCreatedConversationRef.current = false;
      lastLoadedConversationIdRef.current = currentConvId;
      return;
    }

    // Only restore if this is a different conversation than we last loaded
    if (currentConvId !== lastLoadedConversationIdRef.current) {
      lastLoadedConversationIdRef.current = currentConvId;

      if (conversation?.messages && conversation.messages.length > 0) {
        // Group messages by sequence number and provider
        const messagesBySeq = new Map<number, Map<string, Message>>();
        const userMessages = new Map<number, Message>();

        conversation.messages.forEach((msg) => {
          if (msg.role === 'user') {
            userMessages.set(msg.sequenceNum, {
              id: msg.messageId,
              role: 'user',
              content: msg.content,
              isStreaming: false,
            });
          } else {
            // Assistant message
            const providerKey = msg.provider || 'openai';
            if (!messagesBySeq.has(msg.sequenceNum)) {
              messagesBySeq.set(msg.sequenceNum, new Map());
            }
            messagesBySeq.get(msg.sequenceNum)!.set(providerKey, {
              id: msg.messageId,
              role: 'assistant',
              content: msg.content,
              thinking: msg.thinking || undefined,
              isStreaming: false,
            });
          }
        });

        // Reconstruct the 3 parallel conversations
        const restored: Message[][] = [[], [], []];
        const providerOrder: ModelProvider[] = ['openai', 'anthropic', 'google'];

        // Get all sequence numbers and sort them
        const seqNums = Array.from(new Set([...userMessages.keys(), ...messagesBySeq.keys()])).sort(
          (a, b) => a - b,
        );

        seqNums.forEach((seqNum) => {
          const userMsg = userMessages.get(seqNum);
          if (userMsg) {
            // Add user message to all columns
            restored.forEach((col) => col.push({ ...userMsg, id: `${userMsg.id}-${col.length}` }));
          }

          const assistantMsgs = messagesBySeq.get(seqNum);
          if (assistantMsgs) {
            providerOrder.forEach((provider, colIdx) => {
              const msg = assistantMsgs.get(provider);
              if (msg) {
                restored[colIdx].push(msg);
              }
            });
          }
        });

        setConversations(restored);
      } else if (!conversation) {
        // Conversation was cleared (deleted or new), clear messages
        setConversations([[], [], []]);
      }
    }
  }, [conversation]);

  // Update assistant messages with streaming text
  useEffect(() => {
    if (!currentAssistantIds) return;

    // Update each column's conversation based on streaming state
    [0, 1, 2].forEach((colIndex) => {
      const streamKey = COLUMN_TO_STREAM_KEY[colIndex];
      const streamState = streamingProviders[streamKey];
      const assistantId = currentAssistantIds[colIndex];
      if (!assistantId) return;

      setConversations((prev) => {
        const newConversations = [...prev];
        newConversations[colIndex] = prev[colIndex].map((msg) =>
          msg.id === assistantId
            ? {
                ...msg,
                content: streamState.text,
                thinking: streamState.thinking || undefined,
                isStreaming: !streamState.isComplete,
                costCredits: streamState.costCredits,
              }
            : msg,
        );
        return newConversations;
      });
    });
  }, [currentAssistantIds, streamingProviders]);

  // Handle streaming completion
  useEffect(() => {
    if (isAllComplete && currentAssistantIds) {
      // Get the final assistant messages from all 3 columns
      const assistantMessages: (Message | undefined)[] = [0, 1, 2].map((colIndex) => {
        const assistantId = currentAssistantIds[colIndex];
        if (!assistantId) return undefined;
        return conversations[colIndex].find((m) => m.id === assistantId);
      });

      setIsStreaming(false);
      setCurrentRequestId(null);
      setCurrentAssistantIds(null);
      resetStreaming();

      // Save messages to conversation (async, don't await)
      const saveMessages = async () => {
        try {
          // Create conversation if needed
          let convId = conversation?.conversationId;
          let isNewConversation = false;
          if (!convId && pendingUserMessageRef.current) {
            // Generate a title from the first user message (truncate to 100 chars)
            const userContent = pendingUserMessageRef.current.content;
            const title =
              userContent.length > 100 ? userContent.slice(0, 100).trimEnd() + '...' : userContent;

            // Mark that we're creating a new conversation to prevent the restore effect from overwriting messages
            justCreatedConversationRef.current = true;

            const newConv = await createConversation(title);
            convId = newConv?.conversationId;
            isNewConversation = true;
            // Update session with new conversation ID
            if (convId) {
              updateMultiAIPreferences({ activeConversationId: convId });
            }
          }

          if (convId && pendingUserMessageRef.current) {
            const messagesToSave: ConversationMessageInput[] = [];

            // Add user message
            messagesToSave.push({
              role: 'user',
              content: pendingUserMessageRef.current.content,
            });

            // Add assistant messages for each provider
            const providerOrder: ModelProvider[] = ['openai', 'anthropic', 'google'];
            assistantMessages.forEach((msg, idx) => {
              if (msg && msg.content) {
                const provider = providerOrder[idx];
                const config = columnConfigs[idx];
                // Look up model name from models array
                const model = models.find((m) => m.entityId === config.modelEntityId);
                messagesToSave.push({
                  role: 'assistant',
                  content: msg.content,
                  thinking: msg.thinking,
                  provider,
                  modelEntityId: config.modelEntityId || undefined,
                  modelName: model?.modelName || undefined,
                });
              }
            });

            // Use API directly for new conversations to avoid stale closure issues
            // (addMessages captures conversation state which may be stale after createConversation)
            if (isNewConversation) {
              await api.addMessages(convId, messagesToSave);
            } else {
              await addMessages(messagesToSave);
            }
            pendingUserMessageRef.current = null;
          }
        } catch (err) {
          console.error('Failed to save messages:', err);
        }
      };
      saveMessages();
    }
  }, [
    isAllComplete,
    currentAssistantIds,
    resetStreaming,
    conversations,
    conversation,
    createConversation,
    addMessages,
    columnConfigs,
    updateMultiAIPreferences,
    models,
  ]);

  // Handle streaming errors
  useEffect(() => {
    if (streamingError && currentAssistantIds) {
      const errorMessage = streamingError.message.toLowerCase().includes('unauthorized')
        ? 'Your session has expired. Please log out and log back in.'
        : `Error: ${streamingError.message}`;

      setConversations((prev) => {
        const newConversations = [...prev];
        [0, 1, 2].forEach((colIndex) => {
          const assistantId = currentAssistantIds[colIndex];
          if (!assistantId) return;

          newConversations[colIndex] = prev[colIndex].map((msg) =>
            msg.id === assistantId ? { ...msg, content: errorMessage, isStreaming: false } : msg,
          );
        });
        return newConversations;
      });

      setIsStreaming(false);
      setCurrentRequestId(null);
      setCurrentAssistantIds(null);
      resetStreaming();
    }
  }, [streamingError, currentAssistantIds, resetStreaming]);

  // Reset conversation
  const handleReset = useCallback(async () => {
    setConversations([[], [], []]);
    setInputValue('');
    setCurrentRequestId(null);
    setCurrentAssistantIds(null);
    setSummaryResult(null);
    setSummaryMessageCount(0);
    setSummaryConversation([]);
    setSummaryInputValue('');
    setAssumptionsResult(null);
    setConsolidatedAssumptions(null);
    setAssumptionClarifications({});
    setEditingAssumptionIndex(null);
    setEnabledPanels([true, true, true]);
    pendingUserMessageRef.current = null;
    lastLoadedConversationIdRef.current = null;
    resetStreaming();

    // Clear current conversation in local state (don't delete from server)
    clearConversation();

    // Update session to clear active conversation
    updateMultiAIPreferences({ activeConversationId: null });

    if (!isMobile) {
      textareaRef.current?.focus();
    }
  }, [resetStreaming, isMobile, clearConversation, updateMultiAIPreferences]);

  // Toggle panel enabled/disabled state
  const togglePanel = useCallback(
    (colIndex: number) => {
      setEnabledPanels((prev) => {
        const newEnabled = [...prev];
        newEnabled[colIndex] = !newEnabled[colIndex];
        updateMultiAIPreferences({ enabledPanels: newEnabled });
        return newEnabled;
      });
    },
    [updateMultiAIPreferences],
  );

  // Count of enabled panels
  const enabledPanelCount = enabledPanels.filter(Boolean).length;

  // Auto-scroll when messages change
  useEffect(() => {
    scrollRefs.forEach((ref) => ref.scrollToBottomIfNeeded());
  }, [conversations, scrollRefs]);

  // Auto-scroll summary tab when content changes
  useEffect(() => {
    summaryScrollToBottomIfNeeded();
  }, [summaryResult, summaryConversation, summaryScrollToBottomIfNeeded]);

  // Focus textarea when streaming completes
  useEffect(() => {
    if (!isStreaming && !isMobile) {
      textareaRef.current?.focus();
    }
  }, [isStreaming, isMobile]);

  // Listen for global history clear event (from Account Settings)
  useEffect(() => {
    const handleHistoryCleared = () => {
      // Clear all conversation and summary state
      setConversations([[], [], []]);
      setInputValue('');
      setCurrentRequestId(null);
      setCurrentAssistantIds(null);
      setSummaryResult(null);
      setSummaryMessageCount(0);
      setSummaryConversation([]);
      setSummaryInputValue('');
      setAssumptionsResult(null);
      setConsolidatedAssumptions(null);
      setAssumptionClarifications({});
      setEditingAssumptionIndex(null);
      pendingUserMessageRef.current = null;
      lastLoadedConversationIdRef.current = null;
      resetStreaming();
      clearConversation();
      // Refresh the conversation list to reflect deletion
      refreshList();
    };

    window.addEventListener('fias:history-cleared', handleHistoryCleared);
    return () => {
      window.removeEventListener('fias:history-cleared', handleHistoryCleared);
    };
  }, [resetStreaming, clearConversation, refreshList]);

  // Auto-resize textarea
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue, adjustTextareaHeight]);

  // Track horizontal scroll position for custom scrollbar
  useEffect(() => {
    const container = panelsContainerRef.current;
    if (!container) return;

    const updateScrollState = () => {
      setScrollState({
        scrollLeft: container.scrollLeft,
        scrollWidth: container.scrollWidth,
        clientWidth: container.clientWidth,
      });
    };

    // Initial update
    updateScrollState();

    // Listen to scroll events
    container.addEventListener('scroll', updateScrollState);
    // Listen to resize events (content or window changes)
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', updateScrollState);
      resizeObserver.disconnect();
    };
  }, [activeTab]); // Re-run when tab changes

  // Custom scrollbar drag handling
  const handleScrollbarDrag = useCallback(
    (clientX: number) => {
      const container = panelsContainerRef.current;
      const track = scrollTrackRef.current;
      if (!container || !track) return;

      const trackRect = track.getBoundingClientRect();
      const trackWidth = trackRect.width;
      const thumbWidth = 32; // Fixed circular thumb size (w-8 = 32px)
      const maxThumbLeft = trackWidth - thumbWidth;

      // Calculate relative position within track
      const relativeX = clientX - trackRect.left - thumbWidth / 2;
      const clampedX = Math.max(0, Math.min(relativeX, maxThumbLeft));

      // Convert to scroll position
      const scrollRatio = clampedX / maxThumbLeft;
      const maxScrollLeft = scrollState.scrollWidth - scrollState.clientWidth;
      container.scrollLeft = scrollRatio * maxScrollLeft;
    },
    [scrollState],
  );

  // Track horizontal scroll position for assumptions tab custom scrollbar
  useEffect(() => {
    const container = assumptionsPanelsRef.current;
    if (!container) return;

    const updateScrollState = () => {
      setAssumptionsScrollState({
        scrollLeft: container.scrollLeft,
        scrollWidth: container.scrollWidth,
        clientWidth: container.clientWidth,
      });
    };

    // Initial update
    updateScrollState();

    // Listen to scroll events
    container.addEventListener('scroll', updateScrollState);
    // Listen to resize events (content or window changes)
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', updateScrollState);
      resizeObserver.disconnect();
    };
  }, [activeTab, assumptionsResult]); // Re-run when tab changes or assumptions load

  // Custom scrollbar drag handling for assumptions tab
  const handleAssumptionsScrollbarDrag = useCallback(
    (clientX: number) => {
      const container = assumptionsPanelsRef.current;
      const track = assumptionsScrollTrackRef.current;
      if (!container || !track) return;

      const trackRect = track.getBoundingClientRect();
      const trackWidth = trackRect.width;
      const thumbWidth = 24; // Fixed circular thumb size (w-6 = 24px)
      const maxThumbLeft = trackWidth - thumbWidth;

      // Calculate relative position within track
      const relativeX = clientX - trackRect.left - thumbWidth / 2;
      const clampedX = Math.max(0, Math.min(relativeX, maxThumbLeft));

      // Convert to scroll position
      const scrollRatio = clampedX / maxThumbLeft;
      const maxScrollLeft = assumptionsScrollState.scrollWidth - assumptionsScrollState.clientWidth;
      container.scrollLeft = scrollRatio * maxScrollLeft;
    },
    [assumptionsScrollState],
  );

  // Handle tier change - resets providers to defaults and sets appropriate models
  const handleTierChange = (tier: ModelTier) => {
    setSelectedTier(tier);
    const tierModels = modelsByTier[tier] || [];
    const defaultProviders: ModelProvider[] = ['openai', 'anthropic', 'google'];

    setColumnConfigs((prev) => {
      const newConfigs = defaultProviders.map((provider, idx) => {
        const modelForTier = tierModels.find((m) => m.provider === provider);
        const modelName = modelForTier?.modelName || '';
        const isGpt4Model = modelName.startsWith('gpt-4');
        const isGpt5Model = modelName.startsWith('gpt-5');

        // Set tier-appropriate max tokens for GPT-4.x models
        // Fast tier (GPT-4.1) uses lower max tokens for speed
        const gpt4MaxOutputTokens =
          tier === 'fast' && isGpt4Model ? 1000 : prev[idx].gpt4MaxOutputTokens;

        // Set default gpt5Reasoning based on the new model (mini/nano default OFF)
        const gpt5Reasoning = isGpt5Model
          ? getDefaultGpt5Reasoning(modelName)
          : prev[idx].gpt5Reasoning;

        return {
          ...prev[idx],
          provider,
          modelEntityId: modelForTier?.entityId || getRecommendedModel(provider)?.entityId || '',
          gpt4MaxOutputTokens,
          gpt5Reasoning,
        };
      });
      // Persist tier and column configs
      updateMultiAIPreferences({
        selectedTier: tier,
        columnConfigs: newConfigs.map((c) => ({
          provider: c.provider,
          modelEntityId: c.modelEntityId,
          showThinking: c.showThinking,
          gpt5Reasoning: c.gpt5Reasoning,
          providerSettings: {
            gpt4MaxOutputTokens: c.gpt4MaxOutputTokens,
            gpt4Temperature: c.gpt4Temperature,
            reasoningEffort: c.reasoningEffort,
            verbosity: c.verbosity,
            gptMaxOutputTokens: c.gptMaxOutputTokens,
            claudeMaxTokens: c.claudeMaxTokens,
            claudeBudgetTokens: c.claudeBudgetTokens,
            claudeTemperature: c.claudeTemperature,
            geminiMaxOutputTokens: c.geminiMaxOutputTokens,
            geminiTemperature: c.geminiTemperature,
            geminiThinkingLevel: c.geminiThinkingLevel,
          },
        })),
      });
      return newConfigs;
    });
  };

  // Handle column provider change
  const handleColumnProviderChange = (colIndex: number, provider: ModelProvider) => {
    setColumnConfigs((prev) => {
      const newConfigs = [...prev];
      // When provider changes, set model based on current tier if set, otherwise recommended
      let modelEntityId = '';
      if (selectedTier) {
        const tierModels = modelsByTier[selectedTier] || [];
        modelEntityId = tierModels.find((m) => m.provider === provider)?.entityId || '';
      }
      if (!modelEntityId) {
        modelEntityId =
          getRecommendedModel(provider)?.entityId ||
          modelsByProvider[provider]?.[0]?.entityId ||
          '';
      }
      // Set default gpt5Reasoning based on the new model
      const model = getModelById(modelEntityId);
      const isGpt5 = model?.modelName?.startsWith('gpt-5') ?? false;
      const gpt5Reasoning = isGpt5
        ? getDefaultGpt5Reasoning(model?.modelName || '')
        : prev[colIndex].gpt5Reasoning;
      newConfigs[colIndex] = { ...newConfigs[colIndex], provider, modelEntityId, gpt5Reasoning };
      // Persist column configs
      updateMultiAIPreferences({
        columnConfigs: newConfigs.map((c) => ({
          provider: c.provider,
          modelEntityId: c.modelEntityId,
          showThinking: c.showThinking,
          gpt5Reasoning: c.gpt5Reasoning,
          providerSettings: {
            gpt4MaxOutputTokens: c.gpt4MaxOutputTokens,
            gpt4Temperature: c.gpt4Temperature,
            reasoningEffort: c.reasoningEffort,
            verbosity: c.verbosity,
            gptMaxOutputTokens: c.gptMaxOutputTokens,
            claudeMaxTokens: c.claudeMaxTokens,
            claudeBudgetTokens: c.claudeBudgetTokens,
            claudeTemperature: c.claudeTemperature,
            geminiMaxOutputTokens: c.geminiMaxOutputTokens,
            geminiTemperature: c.geminiTemperature,
            geminiThinkingLevel: c.geminiThinkingLevel,
          },
        })),
      });
      return newConfigs;
    });
  };

  // Handle column model change - clears tier since user is customizing
  const handleColumnModelChange = (colIndex: number, modelEntityId: string) => {
    setSelectedTier(null); // Clear tier when manually selecting a model
    setColumnConfigs((prev) => {
      const newConfigs = [...prev];
      // Set default gpt5Reasoning based on the new model
      const model = getModelById(modelEntityId);
      const isGpt5 = model?.modelName?.startsWith('gpt-5') ?? false;
      const gpt5Reasoning = isGpt5
        ? getDefaultGpt5Reasoning(model?.modelName || '')
        : prev[colIndex].gpt5Reasoning;
      newConfigs[colIndex] = { ...newConfigs[colIndex], modelEntityId, gpt5Reasoning };
      // Persist tier (null) and column configs
      updateMultiAIPreferences({
        selectedTier: null,
        columnConfigs: newConfigs.map((c) => ({
          provider: c.provider,
          modelEntityId: c.modelEntityId,
          showThinking: c.showThinking,
          gpt5Reasoning: c.gpt5Reasoning,
          providerSettings: {
            gpt4MaxOutputTokens: c.gpt4MaxOutputTokens,
            gpt4Temperature: c.gpt4Temperature,
            reasoningEffort: c.reasoningEffort,
            verbosity: c.verbosity,
            gptMaxOutputTokens: c.gptMaxOutputTokens,
            claudeMaxTokens: c.claudeMaxTokens,
            claudeBudgetTokens: c.claudeBudgetTokens,
            claudeTemperature: c.claudeTemperature,
            geminiMaxOutputTokens: c.geminiMaxOutputTokens,
            geminiTemperature: c.geminiTemperature,
            geminiThinkingLevel: c.geminiThinkingLevel,
          },
        })),
      });
      return newConfigs;
    });
  };

  // Update column config with partial updates and persist to session
  const updateColumnConfig = (colIndex: number, updates: Partial<ColumnConfig>) => {
    setColumnConfigs((prev) => {
      const newConfigs = [...prev];
      newConfigs[colIndex] = { ...newConfigs[colIndex], ...updates };
      // Persist column configs to session
      updateMultiAIPreferences({
        columnConfigs: newConfigs.map((c) => ({
          provider: c.provider,
          modelEntityId: c.modelEntityId,
          showThinking: c.showThinking,
          gpt5Reasoning: c.gpt5Reasoning,
          providerSettings: {
            gpt4MaxOutputTokens: c.gpt4MaxOutputTokens,
            gpt4Temperature: c.gpt4Temperature,
            reasoningEffort: c.reasoningEffort,
            verbosity: c.verbosity,
            gptMaxOutputTokens: c.gptMaxOutputTokens,
            claudeMaxTokens: c.claudeMaxTokens,
            claudeBudgetTokens: c.claudeBudgetTokens,
            claudeTemperature: c.claudeTemperature,
            geminiMaxOutputTokens: c.geminiMaxOutputTokens,
            geminiTemperature: c.geminiTemperature,
            geminiThinkingLevel: c.geminiThinkingLevel,
          },
        })),
      });
      return newConfigs;
    });
  };

  // Update per-model params for a specific column
  const updateColumnModelParams = useCallback(
    (colIndex: number, modelId: string, params: Record<string, unknown>) => {
      setAllColumnModelParams((prev) => {
        const newParams = [...prev] as typeof prev;
        newParams[colIndex] = {
          ...newParams[colIndex],
          [modelId]: params,
        };
        return newParams;
      });
    },
    [],
  );

  // Handle submit
  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    addBreadcrumb('multi-ai', 'handleSubmit called');

    const trimmedInput = inputValue.trim();
    addBreadcrumb(
      'multi-ai',
      `Submit: hasInput=${!!trimmedInput}, isStreaming=${isStreaming}, isConnected=${isConnected}`,
    );

    if (!trimmedInput || isStreaming) {
      addBreadcrumb('multi-ai', 'Early return - trimmedInput empty or already streaming');
      return;
    }

    // Ensure WebSocket is connected before proceeding
    if (!isConnected) {
      addBreadcrumb('multi-ai', 'WebSocket not connected, connecting...');
      try {
        await connectWebSocket();
        addBreadcrumb('multi-ai', 'WebSocket connected');
      } catch (err) {
        addBreadcrumb('multi-ai', `WebSocket connection failed: ${err}`);
        return;
      }
    }

    // Format file context if any files are attached
    const fileContextPrefix = formatFileContext();
    const hasFileContext = fileContextPrefix.length > 0;

    // Build content for API (includes file context)
    const contentForApi = hasFileContext
      ? `${fileContextPrefix}User query: ${trimmedInput}`
      : trimmedInput;

    const timestamp = Date.now();
    const userMessageId = `user-${timestamp}`;

    // Create assistant message IDs for each column
    const newAssistantIds: string[] = [
      `assistant-0-${timestamp}`,
      `assistant-1-${timestamp}`,
      `assistant-2-${timestamp}`,
    ];

    // Add user message
    // - content: displayed in UI (clean user input)
    // - contextContent: used for conversation history (includes file context if any)
    const userMessage: Message = {
      id: userMessageId,
      role: 'user',
      content: trimmedInput,
      ...(hasFileContext && { contextContent: contentForApi }),
    };

    // Track user message for saving after streaming completes
    pendingUserMessageRef.current = { content: trimmedInput };

    // Update conversations with new messages for each enabled column
    setConversations((prev) => {
      return prev.map((colConversation, colIndex) => {
        if (!enabledPanels[colIndex]) {
          return colConversation; // Don't add messages to disabled panels
        }
        return [
          ...colConversation,
          userMessage,
          {
            id: newAssistantIds[colIndex],
            role: 'assistant' as const,
            content: '',
            isStreaming: true,
          },
        ];
      });
    });

    setInputValue('');
    // Clear attached files from UI after submission (context is preserved in message history)
    if (hasFileContext) {
      clearAttachedFiles();
    }
    setIsStreaming(true);
    addBreadcrumb('multi-ai', 'setIsStreaming(true) called');
    setCurrentAssistantIds(newAssistantIds);
    // Don't clear summary - it persists and shows "Update" button if conversation changed
    setAssumptionsResult(null);
    setConsolidatedAssumptions(null);
    setAssumptionClarifications({});

    // Build conversation history for a column
    // Use contextContent (with file context) if available, otherwise use content
    const buildHistory = (colIndex: number) => {
      if (!includeContext) {
        // Only send the current user message (with file context if any)
        return [{ role: userMessage.role, content: contentForApi }];
      }
      const colMessages = conversations[colIndex].filter((m) => m.content.trim().length > 0);
      const allMessages = [...colMessages, userMessage];
      const truncated = allMessages.slice(-MAX_CONVERSATION_MESSAGES);
      return truncated.map((m) => ({ role: m.role, content: m.contextContent || m.content }));
    };

    const traceId = traceStart('multi-ai.submit', { columns: columnConfigs });

    try {
      addBreadcrumb('multi-ai', 'Calling api.startArche');
      // Get actual model names from entities for the API call (only for enabled panels)
      const model0 = enabledPanels[0] ? getModelById(columnConfigs[0].modelEntityId) : null;
      const model1 = enabledPanels[1] ? getModelById(columnConfigs[1].modelEntityId) : null;
      const model2 = enabledPanels[2] ? getModelById(columnConfigs[2].modelEntityId) : null;

      // Build model parameters for a column based on its provider/model
      const buildModelParams = (colIndex: number) => {
        const config = columnConfigs[colIndex];
        const model = getModelById(config.modelEntityId);
        const modelName = model?.modelName || '';

        // GPT-4.x parameters
        if (modelName.startsWith('gpt-4') || modelName.startsWith('gpt-4o')) {
          return {
            maxOutputTokens: config.gpt4MaxOutputTokens,
            temperature: config.gpt4Temperature,
          };
        }

        // GPT-5 parameters
        if (modelName.startsWith('gpt-5')) {
          return {
            showThinking: config.showThinking,
            reasoningEnabled: config.gpt5Reasoning,
            reasoningEffort: config.reasoningEffort,
            verbosity: config.verbosity,
            maxOutputTokens: config.gptMaxOutputTokens,
          };
        }

        // Grok parameters (uses gpt5Params on backend)
        if (modelName.includes('grok')) {
          return {
            showThinking: config.showThinking,
            reasoningEnabled: config.gpt5Reasoning,
            reasoningEffort: config.reasoningEffort,
            verbosity: config.verbosity,
            maxOutputTokens: config.gptMaxOutputTokens,
          };
        }

        // Claude parameters
        if (modelName.includes('claude')) {
          return {
            thinkingEnabled: config.showThinking,
            maxTokens: config.claudeMaxTokens,
            ...(config.showThinking && { budgetTokens: config.claudeBudgetTokens }),
            temperature: config.showThinking ? 1 : config.claudeTemperature,
          };
        }

        // Gemini parameters
        if (modelName.includes('gemini')) {
          // Gemini 2.5+ supports thinking (matches gemini-2.5, gemini-3, etc.)
          const isGemini25Plus = modelName.match(/gemini-(2\.5|[3-9]|[1-9]\d)/) !== null;
          return {
            thinkingEnabled: isGemini25Plus && config.showThinking,
            maxOutputTokens: config.geminiMaxOutputTokens,
            temperature: config.geminiTemperature,
            ...(isGemini25Plus && { thinkingLevel: config.geminiThinkingLevel }),
          };
        }

        return { showThinking: config.showThinking };
      };

      // Pass column configurations - the API uses openai/anthropic/google keys but
      // we map our columns to them (column 0 → openai slot, etc.)
      // Disabled panels get empty messages and empty model to skip processing
      const result = await api.startArche('arche_multi_ai', {
        openaiMessages: enabledPanels[0] ? buildHistory(0) : [],
        anthropicMessages: enabledPanels[1] ? buildHistory(1) : [],
        googleMessages: enabledPanels[2] ? buildHistory(2) : [],
        openaiModel: model0?.modelName || '',
        anthropicModel: model1?.modelName || '',
        googleModel: model2?.modelName || '',
        // Pass actual provider for each slot so backend knows which API to call
        openaiProvider: enabledPanels[0] ? columnConfigs[0].provider : '',
        anthropicProvider: enabledPanels[1] ? columnConfigs[1].provider : '',
        googleProvider: enabledPanels[2] ? columnConfigs[2].provider : '',
        // Pass model parameters for each column
        openaiParams: enabledPanels[0] ? buildModelParams(0) : {},
        anthropicParams: enabledPanels[1] ? buildModelParams(1) : {},
        googleParams: enabledPanels[2] ? buildModelParams(2) : {},
      });

      addBreadcrumb(
        'multi-ai',
        `API returned requestId: ${result.requestId}, status: ${result.status}`,
      );
      setCurrentRequestId(result.requestId);
      addBreadcrumb('multi-ai', `setCurrentRequestId called with: ${result.requestId}`);
      traceEnd(traceId, { requestId: result.requestId });
    } catch (err) {
      addBreadcrumb('multi-ai', `API error: ${err instanceof Error ? err.message : String(err)}`);
      traceError(traceId, err instanceof Error ? err : new Error(String(err)));

      const errorMessage = 'Sorry, something went wrong. Please try again.';
      setConversations((prev) =>
        prev.map((colConversation, colIndex) =>
          colConversation.map((msg) =>
            msg.id === newAssistantIds[colIndex]
              ? { ...msg, content: errorMessage, isStreaming: false }
              : msg,
          ),
        ),
      );

      setIsStreaming(false);
      setCurrentAssistantIds(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Handler to submit a query directly (used by learn more feature)
  const submitQueryDirectly = useCallback(
    async (query: string) => {
      if (!query.trim() || isStreaming) return;

      // Ensure WebSocket is connected
      if (!isConnected) {
        try {
          await connectWebSocket();
        } catch {
          return;
        }
      }

      const timestamp = Date.now();
      const userMessageId = `user-${timestamp}`;
      const newAssistantIds: string[] = [
        `assistant-0-${timestamp}`,
        `assistant-1-${timestamp}`,
        `assistant-2-${timestamp}`,
      ];

      const userMessage: Message = {
        id: userMessageId,
        role: 'user',
        content: query.trim(),
      };

      // Track user message for saving after streaming completes
      pendingUserMessageRef.current = { content: query.trim() };

      // Update conversations with new messages for each enabled column
      setConversations((prev) => {
        return prev.map((colConversation, colIndex) => {
          if (!enabledPanels[colIndex]) {
            return colConversation;
          }
          return [
            ...colConversation,
            userMessage,
            {
              id: newAssistantIds[colIndex],
              role: 'assistant' as const,
              content: '',
              isStreaming: true,
            },
          ];
        });
      });

      setInputValue('');
      setIsStreaming(true);
      setCurrentAssistantIds(newAssistantIds);
      setAssumptionsResult(null);
      setConsolidatedAssumptions(null);
      setAssumptionClarifications({});

      // Scroll all columns to bottom so user can see the new messages
      setTimeout(() => {
        scrollRefs.forEach((ref) => ref.scrollToBottom());
      }, 50);

      // Build conversation history for a column
      const buildHistory = (colIndex: number) => {
        if (!includeContext) {
          return [{ role: userMessage.role, content: userMessage.content }];
        }
        const messages = conversations[colIndex].filter((m) => m.content.trim().length > 0);
        const allMessages = [...messages, userMessage];
        const truncated = allMessages.slice(-MAX_CONVERSATION_MESSAGES);
        return truncated.map((m) => ({ role: m.role, content: m.content }));
      };

      // Get model info for each column
      const model0 = enabledPanels[0] ? getModelById(columnConfigs[0].modelEntityId) : null;
      const model1 = enabledPanels[1] ? getModelById(columnConfigs[1].modelEntityId) : null;
      const model2 = enabledPanels[2] ? getModelById(columnConfigs[2].modelEntityId) : null;

      // Build model parameters for a column based on its provider/model
      const buildModelParams = (colIndex: number) => {
        const config = columnConfigs[colIndex];
        const model = getModelById(config.modelEntityId);
        const modelName = model?.modelName || '';

        // GPT-4.x parameters
        if (modelName.startsWith('gpt-4') || modelName.startsWith('gpt-4o')) {
          return {
            maxOutputTokens: config.gpt4MaxOutputTokens,
            temperature: config.gpt4Temperature,
          };
        }

        // GPT-5 parameters
        if (modelName.startsWith('gpt-5')) {
          return {
            showThinking: config.showThinking,
            reasoningEnabled: config.gpt5Reasoning,
            reasoningEffort: config.reasoningEffort,
            verbosity: config.verbosity,
            maxOutputTokens: config.gptMaxOutputTokens,
          };
        }

        // Grok parameters (uses gpt5Params on backend)
        if (modelName.includes('grok')) {
          return {
            showThinking: config.showThinking,
            reasoningEnabled: config.gpt5Reasoning,
            reasoningEffort: config.reasoningEffort,
            verbosity: config.verbosity,
            maxOutputTokens: config.gptMaxOutputTokens,
          };
        }

        // Claude parameters
        if (modelName.includes('claude')) {
          return {
            thinkingEnabled: config.showThinking,
            maxTokens: config.claudeMaxTokens,
            ...(config.showThinking && { budgetTokens: config.claudeBudgetTokens }),
            temperature: config.showThinking ? 1 : config.claudeTemperature,
          };
        }

        // Gemini parameters
        if (modelName.includes('gemini')) {
          const isGemini25Plus = modelName.match(/gemini-(2\.5|[3-9]|[1-9]\d)/) !== null;
          return {
            thinkingEnabled: isGemini25Plus && config.showThinking,
            maxOutputTokens: config.geminiMaxOutputTokens,
            temperature: config.geminiTemperature,
            ...(isGemini25Plus && { thinkingLevel: config.geminiThinkingLevel }),
          };
        }

        return { showThinking: config.showThinking };
      };

      try {
        const result = await api.startArche('arche_multi_ai', {
          openaiMessages: enabledPanels[0] ? buildHistory(0) : [],
          anthropicMessages: enabledPanels[1] ? buildHistory(1) : [],
          googleMessages: enabledPanels[2] ? buildHistory(2) : [],
          openaiModel: model0?.modelName || '',
          anthropicModel: model1?.modelName || '',
          googleModel: model2?.modelName || '',
          openaiProvider: enabledPanels[0] ? columnConfigs[0].provider : '',
          anthropicProvider: enabledPanels[1] ? columnConfigs[1].provider : '',
          googleProvider: enabledPanels[2] ? columnConfigs[2].provider : '',
          openaiParams: enabledPanels[0] ? buildModelParams(0) : {},
          anthropicParams: enabledPanels[1] ? buildModelParams(1) : {},
          googleParams: enabledPanels[2] ? buildModelParams(2) : {},
        });

        setCurrentRequestId(result.requestId);
      } catch {
        setIsStreaming(false);
        setCurrentAssistantIds(null);
      }
    },
    [
      isStreaming,
      isConnected,
      connectWebSocket,
      conversations,
      enabledPanels,
      columnConfigs,
      includeContext,
      getModelById,
      scrollRefs,
    ],
  );

  // Text highlight "Learn More" feature
  const highlight = useTextHighlightLearnMore(
    messagesContainerRef,
    submitQueryDirectly,
    isStreaming,
  );

  const handleStop = useCallback(() => {
    // Unsubscribe from WebSocket to stop receiving tokens
    unsubscribeStreaming();
    setCurrentRequestId(null);
    setCurrentAssistantIds(null);
    setIsStreaming(false);

    // Clean up conversations based on whether each column received tokens
    // For columns that received tokens: keep everything, just mark as not streaming
    // For columns that never received tokens: remove the empty assistant AND its user message
    // This prevents history pollution where the next query includes orphaned user messages
    setConversations((prev) =>
      prev.map((colConversation) => {
        // Find the last streaming assistant message (search from end)
        let lastAssistantIndex = -1;
        for (let i = colConversation.length - 1; i >= 0; i--) {
          if (colConversation[i].role === 'assistant' && colConversation[i].isStreaming) {
            lastAssistantIndex = i;
            break;
          }
        }

        if (lastAssistantIndex === -1) {
          // No streaming assistant message, nothing to clean up
          return colConversation;
        }

        const lastAssistant = colConversation[lastAssistantIndex];
        const hasContent = lastAssistant.content.trim().length > 0;

        if (hasContent) {
          // Column received tokens - keep everything, just mark as not streaming
          return colConversation.map((m) => (m.isStreaming ? { ...m, isStreaming: false } : m));
        } else {
          // Column never received tokens - remove the empty assistant AND its user message
          const userMessageIndex = lastAssistantIndex - 1;
          return colConversation.filter((_, index) => {
            // Remove the empty streaming assistant
            if (index === lastAssistantIndex) return false;
            // Remove the associated user message (if it exists and is right before)
            if (index === userMessageIndex && colConversation[index].role === 'user') return false;
            return true;
          });
        }
      }),
    );
  }, [unsubscribeStreaming]);

  // Format a conversation as a readable string for summary/comparison
  const formatConversation = (colIndex: number): string => {
    return conversations[colIndex]
      .filter((m) => m.content.trim())
      .map((m) => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.content}`)
      .join('\n\n');
  };

  // Check if all enabled columns have at least one assistant response
  const hasAllResponses = (): boolean => {
    return (
      enabledPanelCount > 0 &&
      conversations.every(
        (colConversation, colIndex) =>
          !enabledPanels[colIndex] ||
          colConversation.some((m) => m.role === 'assistant' && m.content.trim()),
      )
    );
  };

  // Handle Summary button - uses WebSocket streaming
  const handleSummarize = async () => {
    if (!hasAllResponses()) return;

    setIsSummarizing(true);
    setSummaryResult(null);

    // Ensure WebSocket is connected
    if (!isConnected) {
      try {
        await connectWebSocket();
      } catch (err) {
        console.error('WebSocket connection failed:', err);
        setSummaryResult('Failed to connect. Please try again.');
        setIsSummarizing(false);
        return;
      }
    }

    try {
      // Build conversation sections only for enabled panels
      const conversationSections = columnConfigs
        .map((config, colIndex) => {
          if (!enabledPanels[colIndex]) return null;
          const model = getModelById(config.modelEntityId);
          const modelName = model?.name || `AI ${colIndex + 1}`;
          return `## ${modelName}:\n${formatConversation(colIndex)}`;
        })
        .filter(Boolean)
        .join('\n\n');

      const aiCount =
        enabledPanelCount === 1
          ? 'this AI conversation'
          : enabledPanelCount === 2
            ? 'these two AI conversations'
            : 'these three AI conversations';

      const summaryPrompt = `Compare and summarize ${aiCount}. Each conversation includes the same user questions but different AI responses.

${conversationSections}

Provide a ${enabledPanelCount > 1 ? 'comparative ' : ''}summary highlighting:
${enabledPanelCount > 1 ? '1. Key similarities in how the AIs approached the questions\n2. Notable differences in their responses\n3. Which AI might be most helpful for different aspects of the conversation' : '1. Key points from the conversation\n2. Main insights and takeaways\n3. Any areas that might benefit from further exploration'}`;

      // Get the selected model for summary
      const summaryModel = summaryModelId ? getModelById(summaryModelId) : null;
      const summaryModelName = summaryModel?.modelName || 'gpt-5.2';

      // Route to the correct provider slot
      const isOpenAI = summaryProvider === 'openai';
      const isAnthropic = summaryProvider === 'anthropic';
      const isGoogle = summaryProvider === 'google';

      // Build params with thinking disabled for all providers
      const buildSummaryParams = () => {
        if (isOpenAI) {
          if (summaryModelName.startsWith('gpt-5')) {
            return {
              openaiParams: {
                showThinking: false,
                reasoningEffort: 'low' as const,
                verbosity: 'medium' as const,
                maxOutputTokens: 4000,
              },
            };
          }
          // GPT-4.x doesn't have showThinking
          return { openaiParams: { maxOutputTokens: 4000 } };
        }
        if (isAnthropic) {
          return {
            anthropicParams: {
              thinkingEnabled: false,
              maxTokens: 4000,
            },
          };
        }
        if (isGoogle) {
          return {
            googleParams: {
              thinkingEnabled: false,
              maxOutputTokens: 4000,
            },
          };
        }
        return {};
      };

      const result = await api.startArche('arche_multi_ai', {
        openaiMessages: isOpenAI ? [{ role: 'user', content: summaryPrompt }] : [],
        anthropicMessages: isAnthropic ? [{ role: 'user', content: summaryPrompt }] : [],
        googleMessages: isGoogle ? [{ role: 'user', content: summaryPrompt }] : [],
        openaiModel: isOpenAI ? summaryModelName : '',
        anthropicModel: isAnthropic ? summaryModelName : '',
        googleModel: isGoogle ? summaryModelName : '',
        openaiProvider: isOpenAI ? summaryProvider : '',
        anthropicProvider: isAnthropic ? summaryProvider : '',
        googleProvider: isGoogle ? summaryProvider : '',
        ...buildSummaryParams(),
      });

      // Use subscribeOneOff to handle the summary streaming
      // Calculate total message count to track when summary was generated
      const totalMessages = conversations.reduce((sum, col) => sum + col.length, 0);

      let summaryText = '';
      // Determine which stream to listen to based on provider
      const expectedStream = isOpenAI ? 'stream-0' : isAnthropic ? 'stream-1' : 'stream-2';
      const cleanup = subscribeOneOff(result.requestId, {
        onToken: (nodeId, text) => {
          if (nodeId === expectedStream) {
            summaryText += text;
            setSummaryResult(summaryText);
          }
        },
        onComplete: () => {
          setIsSummarizing(false);
          setSummaryMessageCount(totalMessages);
          summaryCleanupRef.current = null;
        },
        onError: (message) => {
          console.error('Summary streaming error:', message);
          setSummaryResult('Failed to generate summary. Please try again.');
          setIsSummarizing(false);
          summaryCleanupRef.current = null;
        },
      });
      summaryCleanupRef.current = cleanup;
    } catch (err) {
      console.error('Summary failed:', err);
      setSummaryResult('Failed to generate summary. Please try again.');
      setIsSummarizing(false);
    }
  };

  // Stop summary generation
  const handleStopSummary = useCallback(() => {
    if (summaryCleanupRef.current) {
      summaryCleanupRef.current();
      summaryCleanupRef.current = null;
    }
    if (summaryConvoCleanupRef.current) {
      summaryConvoCleanupRef.current();
      summaryConvoCleanupRef.current = null;
    }
    setIsSummarizing(false);
    setIsSummaryStreaming(false);
  }, []);

  // Handle submitting a question about the summary
  const handleSummaryQuestion = async (e?: React.FormEvent) => {
    e?.preventDefault();

    const trimmedInput = summaryInputValue.trim();
    if (!trimmedInput || isSummaryStreaming || !summaryResult) return;

    // Ensure WebSocket is connected
    if (!isConnected) {
      try {
        await connectWebSocket();
      } catch (err) {
        console.error('WebSocket connection failed:', err);
        return;
      }
    }

    const timestamp = Date.now();
    const userMessageId = `summary-user-${timestamp}`;
    const assistantMessageId = `summary-assistant-${timestamp}`;

    const userMessage: Message = {
      id: userMessageId,
      role: 'user',
      content: trimmedInput,
    };

    const assistantPlaceholder: Message = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      isStreaming: true,
    };

    setSummaryConversation((prev) => [...prev, userMessage, assistantPlaceholder]);
    setSummaryInputValue('');
    setIsSummaryStreaming(true);

    // Build conversation history for the summary discussion
    // Include the original summary as context
    const messages = [
      { role: 'assistant' as const, content: summaryResult },
      ...summaryConversation
        .filter((m) => m.content.trim())
        .map((m) => ({ role: m.role, content: m.content })),
      { role: 'user' as const, content: trimmedInput },
    ];

    try {
      const result = await api.startArche('arche_multi_ai', {
        openaiMessages: messages,
        anthropicMessages: [],
        googleMessages: [],
        openaiModel: 'gpt-5.2',
        anthropicModel: '',
        googleModel: '',
      });

      let responseText = '';
      const cleanup = subscribeOneOff(result.requestId, {
        onToken: (nodeId, text) => {
          if (nodeId === 'stream-0') {
            responseText += text;
            setSummaryConversation((prev) =>
              prev.map((msg) =>
                msg.id === assistantMessageId ? { ...msg, content: responseText } : msg,
              ),
            );
          }
        },
        onComplete: () => {
          setSummaryConversation((prev) =>
            prev.map((msg) =>
              msg.id === assistantMessageId ? { ...msg, isStreaming: false } : msg,
            ),
          );
          setIsSummaryStreaming(false);
          summaryConvoCleanupRef.current = null;
        },
        onError: (message) => {
          console.error('Summary conversation error:', message);
          setSummaryConversation((prev) =>
            prev.map((msg) =>
              msg.id === assistantMessageId
                ? { ...msg, content: 'Failed to get response.', isStreaming: false }
                : msg,
            ),
          );
          setIsSummaryStreaming(false);
          summaryConvoCleanupRef.current = null;
        },
      });
      summaryConvoCleanupRef.current = cleanup;
    } catch (err) {
      console.error('Summary question failed:', err);
      setSummaryConversation((prev) =>
        prev.map((msg) =>
          msg.id === assistantMessageId
            ? { ...msg, content: 'Failed to send question.', isStreaming: false }
            : msg,
        ),
      );
      setIsSummaryStreaming(false);
    }
  };

  const handleSummaryKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSummaryQuestion();
    }
  };

  // Consolidate assumptions from all columns using the consolidator entity
  const consolidateAssumptions = async (rawAssumptions: string[]) => {
    // Only consolidate if we have at least one enabled panel with assumptions
    const enabledAssumptions = rawAssumptions.filter(
      (_, idx) => enabledPanels[idx] && rawAssumptions[idx].trim(),
    );
    if (enabledAssumptions.length === 0) {
      setIsConsolidating(false);
      return;
    }

    setIsConsolidating(true);
    addBreadcrumb('multi-ai', 'Starting assumption consolidation');

    // Build assumption sections only for enabled panels
    const assumptionSections = columnConfigs
      .map((config, colIndex) => {
        if (!enabledPanels[colIndex] || !rawAssumptions[colIndex].trim()) return null;
        const label = getProviderLabel(config.provider);
        return `## ${label} (col${colIndex}) Assumptions:\n${rawAssumptions[colIndex]}`;
      })
      .filter(Boolean)
      .join('\n\n');

    // Build JSON format description based on enabled panels
    const jsonKeys = columnConfigs
      .map((_, colIndex) => (enabledPanels[colIndex] ? `"col${colIndex}": true/false` : null))
      .filter(Boolean)
      .join(', ');

    try {
      // Create a consolidation prompt that will be processed by gpt-5.2
      const consolidationPrompt = `You are an expert at analyzing and consolidating assumptions from multiple AI responses. Your task is to:
1. Read the assumptions listed by each AI
2. Identify unique assumptions across all responses
3. Merge assumptions that are essentially the same but worded differently into a single assumption
4. Track which AI made each assumption

You MUST respond with ONLY a valid JSON object, no other text. The format must be exactly:
{"assumptions": [{"assumption": "text", ${jsonKeys}}, ...]}

Be thorough - list ALL assumptions mentioned by any AI. If an assumption is similar between AIs, consolidate it into one entry and mark all that made it as true.

${assumptionSections}

Analyze these assumptions and return a consolidated JSON list:`;

      // Get the selected model for consolidation
      const consolidationModel = consolidationModelId ? getModelById(consolidationModelId) : null;
      const consolidationModelName = consolidationModel?.modelName || 'gpt-5.2';

      // Route to the correct provider slot
      const isOpenAI = consolidationProvider === 'openai';
      const isAnthropic = consolidationProvider === 'anthropic';
      const isGoogle = consolidationProvider === 'google';

      // Build params with thinking disabled for the selected provider
      const buildConsolidationParams = () => {
        if (isOpenAI) {
          if (consolidationModelName.startsWith('gpt-5')) {
            return {
              openaiParams: {
                showThinking: false,
                reasoningEffort: 'low' as const,
                verbosity: 'low' as const,
                maxOutputTokens: 16000,
              },
            };
          }
          // GPT-4.x doesn't have showThinking
          return { openaiParams: { maxOutputTokens: 16000 } };
        }
        if (isAnthropic) {
          return {
            anthropicParams: {
              thinkingEnabled: false,
              maxTokens: 16000,
            },
          };
        }
        if (isGoogle) {
          return {
            googleParams: {
              thinkingEnabled: false,
              maxOutputTokens: 16000,
            },
          };
        }
        return {};
      };

      const result = await api.startArche('arche_multi_ai', {
        openaiMessages: isOpenAI ? [{ role: 'user', content: consolidationPrompt }] : [],
        anthropicMessages: isAnthropic ? [{ role: 'user', content: consolidationPrompt }] : [],
        googleMessages: isGoogle ? [{ role: 'user', content: consolidationPrompt }] : [],
        openaiModel: isOpenAI ? consolidationModelName : '',
        anthropicModel: isAnthropic ? consolidationModelName : '',
        googleModel: isGoogle ? consolidationModelName : '',
        openaiProvider: isOpenAI ? consolidationProvider : '',
        anthropicProvider: isAnthropic ? consolidationProvider : '',
        googleProvider: isGoogle ? consolidationProvider : '',
        ...buildConsolidationParams(),
      });

      // Use subscribeOneOff to handle the consolidation streaming
      // Determine which stream to listen to based on provider
      const expectedStream = isOpenAI ? 'stream-0' : isAnthropic ? 'stream-1' : 'stream-2';
      let consolidationText = '';
      setConsolidationStreamText(''); // Clear previous stream text
      const cleanup = subscribeOneOff(result.requestId, {
        onToken: (nodeId, text) => {
          if (nodeId === expectedStream) {
            consolidationText += text;
            setConsolidationStreamText(consolidationText); // Update UI with streaming text
          }
        },
        onComplete: () => {
          addBreadcrumb(
            'multi-ai',
            `Consolidation complete, parsing JSON: ${consolidationText.substring(0, 100)}...`,
          );
          try {
            // Parse the JSON response
            const parsed = JSON.parse(consolidationText);
            if (parsed.assumptions && Array.isArray(parsed.assumptions)) {
              setConsolidatedAssumptions(parsed.assumptions);
            } else {
              console.error('Invalid consolidation response format:', parsed);
              setConsolidatedAssumptions(null);
            }
          } catch (parseErr) {
            console.error('Failed to parse consolidation response:', parseErr);
            // Try to extract JSON from the response if it contains extra text
            const jsonMatch = consolidationText.match(/\{[\s\S]*\}/);
            if (jsonMatch) {
              try {
                const parsed = JSON.parse(jsonMatch[0]);
                if (parsed.assumptions && Array.isArray(parsed.assumptions)) {
                  setConsolidatedAssumptions(parsed.assumptions);
                  setConsolidationStreamText(''); // Clear stream text on success
                  setIsConsolidating(false);
                  consolidationCleanupRef.current = null;
                  return;
                }
              } catch {
                // Ignore secondary parse error
              }
            }
            setConsolidatedAssumptions(null);
          }
          setConsolidationStreamText(''); // Clear stream text when done
          setIsConsolidating(false);
          consolidationCleanupRef.current = null;
        },
        onError: (message) => {
          console.error('Consolidation streaming error:', message);
          setConsolidationStreamText(''); // Clear stream text on error
          setIsConsolidating(false);
          consolidationCleanupRef.current = null;
        },
      });
      consolidationCleanupRef.current = cleanup;
    } catch (err) {
      console.error('Consolidation failed:', err);
      setIsConsolidating(false);
    }
  };

  // Handle Check Assumptions button - uses WebSocket streaming
  const handleCheckAssumptions = async () => {
    if (!hasAllResponses()) return;

    setIsCheckingAssumptions(true);
    setAssumptionsResult(null);
    setConsolidatedAssumptions(null);

    // Ensure WebSocket is connected
    if (!isConnected) {
      try {
        await connectWebSocket();
      } catch (err) {
        console.error('WebSocket connection failed:', err);
        setAssumptionsResult(['Failed to connect', 'Failed to connect', 'Failed to connect']);
        setIsCheckingAssumptions(false);
        return;
      }
    }

    try {
      const assumptionPrompt = `In your previous responses to my questions, please list all assumptions you made in providing your answers. Be specific and comprehensive in listing all assumptions one by one.`;

      // Build full conversation history for each column, then add the assumption prompt
      // Only for enabled panels
      const buildAssumptionMessages = (colIndex: number) => {
        if (!enabledPanels[colIndex]) return [];
        const history = conversations[colIndex]
          .filter((m) => m.content.trim().length > 0)
          .map((m) => ({ role: m.role, content: m.content }));
        return [...history, { role: 'user' as const, content: assumptionPrompt }];
      };

      // Get actual model names from entities for the API call (only for enabled panels)
      const model0 = enabledPanels[0] ? getModelById(columnConfigs[0].modelEntityId) : null;
      const model1 = enabledPanels[1] ? getModelById(columnConfigs[1].modelEntityId) : null;
      const model2 = enabledPanels[2] ? getModelById(columnConfigs[2].modelEntityId) : null;

      // Build params using same settings as responses, but with thinking disabled
      const buildAssumptionParams = (colIndex: number) => {
        const config = columnConfigs[colIndex];
        const model = getModelById(config.modelEntityId);
        const modelName = model?.modelName || '';

        if (!modelName) return {};

        // GPT-4.x parameters
        if (modelName.startsWith('gpt-4') || modelName.startsWith('gpt-4o')) {
          return {
            maxOutputTokens: config.gpt4MaxOutputTokens,
            temperature: config.gpt4Temperature,
          };
        }

        // GPT-5 parameters - same settings but thinking off
        if (modelName.startsWith('gpt-5')) {
          return {
            showThinking: false,
            reasoningEffort: config.reasoningEffort,
            verbosity: config.verbosity,
            maxOutputTokens: config.gptMaxOutputTokens,
          };
        }

        // Grok parameters - same settings but thinking off
        if (modelName.includes('grok')) {
          return {
            showThinking: false,
            reasoningEffort: config.reasoningEffort,
            verbosity: config.verbosity,
            maxOutputTokens: config.gptMaxOutputTokens,
          };
        }

        // Claude parameters - same settings but thinking off
        if (modelName.includes('claude')) {
          return {
            thinkingEnabled: false,
            maxTokens: config.claudeMaxTokens,
            temperature: config.claudeTemperature,
          };
        }

        // Gemini parameters - same settings but thinking off
        if (modelName.includes('gemini')) {
          return {
            thinkingEnabled: false,
            maxOutputTokens: config.geminiMaxOutputTokens,
            temperature: config.geminiTemperature,
          };
        }

        return {};
      };

      const result = await api.startArche('arche_multi_ai', {
        openaiMessages: buildAssumptionMessages(0),
        anthropicMessages: buildAssumptionMessages(1),
        googleMessages: buildAssumptionMessages(2),
        openaiModel: model0?.modelName || '',
        anthropicModel: model1?.modelName || '',
        googleModel: model2?.modelName || '',
        openaiProvider: enabledPanels[0] ? columnConfigs[0].provider : '',
        anthropicProvider: enabledPanels[1] ? columnConfigs[1].provider : '',
        googleProvider: enabledPanels[2] ? columnConfigs[2].provider : '',
        openaiParams: buildAssumptionParams(0),
        anthropicParams: buildAssumptionParams(1),
        googleParams: buildAssumptionParams(2),
      });

      // Use subscribeOneOff to handle the assumptions streaming
      const assumptionTexts = ['', '', ''];
      const cleanup = subscribeOneOff(result.requestId, {
        onToken: (nodeId, text) => {
          if (nodeId === 'stream-0') {
            assumptionTexts[0] += text;
          } else if (nodeId === 'stream-1') {
            assumptionTexts[1] += text;
          } else if (nodeId === 'stream-2') {
            assumptionTexts[2] += text;
          }
          setAssumptionsResult([...assumptionTexts]);
        },
        onComplete: () => {
          setIsCheckingAssumptions(false);
          assumptionsCleanupRef.current = null;
          // After raw assumptions complete, consolidate them
          consolidateAssumptions(assumptionTexts);
        },
        onError: (message) => {
          console.error('Assumptions streaming error:', message);
          setAssumptionsResult(['Failed to check', 'Failed to check', 'Failed to check']);
          setIsCheckingAssumptions(false);
          assumptionsCleanupRef.current = null;
        },
      });
      assumptionsCleanupRef.current = cleanup;
    } catch (err) {
      console.error('Check assumptions failed:', err);
      setAssumptionsResult(['Failed to check', 'Failed to check', 'Failed to check']);
      setIsCheckingAssumptions(false);
    }
  };

  // Stop assumptions checking and/or consolidation
  const handleStopAssumptions = useCallback(() => {
    // Stop assumptions gathering if in progress
    if (assumptionsCleanupRef.current) {
      assumptionsCleanupRef.current();
      assumptionsCleanupRef.current = null;
    }
    // Stop consolidation if in progress
    if (consolidationCleanupRef.current) {
      consolidationCleanupRef.current();
      consolidationCleanupRef.current = null;
    }
    // Stop resend if in progress
    if (resendCleanupRef.current) {
      resendCleanupRef.current();
      resendCleanupRef.current = null;
    }
    setIsCheckingAssumptions(false);
    setIsConsolidating(false);
    setIsResendingWithClarifications(false);
  }, []);

  // Handle updating a clarification for an assumption
  const handleClarificationChange = (index: number, value: string) => {
    setAssumptionClarifications((prev) => ({
      ...prev,
      [index]: value,
    }));
  };

  // Handle removing a clarification
  const handleRemoveClarification = (index: number) => {
    setAssumptionClarifications((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
    setEditingAssumptionIndex(null);
  };

  // Check if any assumptions have clarifications
  const hasClarifications =
    Object.keys(assumptionClarifications).length > 0 &&
    Object.values(assumptionClarifications).some((v) => v.trim().length > 0);

  // Handle resending to AIs with clarified assumptions
  const handleResendWithClarifications = async () => {
    if (!consolidatedAssumptions || !hasClarifications) return;

    setIsResendingWithClarifications(true);
    setEditingAssumptionIndex(null);
    // Switch to responses tab so user can see the new responses coming in
    setActiveTab('responses');

    // Ensure WebSocket is connected
    if (!isConnected) {
      try {
        await connectWebSocket();
      } catch (err) {
        console.error('WebSocket connection failed:', err);
        setIsResendingWithClarifications(false);
        return;
      }
    }

    // Build the clarifications prompt
    const clarificationsList = consolidatedAssumptions
      .map((assumption, index) => {
        const clarification = assumptionClarifications[index]?.trim();
        if (clarification) {
          return `- "${assumption.assumption}" → Clarification: ${clarification}`;
        }
        return null;
      })
      .filter(Boolean)
      .join('\n');

    const clarificationPrompt = `Based on our previous conversation, I'd like to clarify some assumptions:

${clarificationsList}

Please revise your previous response taking these clarifications into account. Be concise and focus on how these clarifications change or refine your answer.`;

    const timestamp = Date.now();
    const newAssistantIds: string[] = [
      `assistant-clarify-0-${timestamp}`,
      `assistant-clarify-1-${timestamp}`,
      `assistant-clarify-2-${timestamp}`,
    ];

    const clarificationMessage: Message = {
      id: `user-clarify-${timestamp}`,
      role: 'user',
      content: clarificationPrompt,
    };

    // Add clarification message and placeholder responses only to enabled columns
    setConversations((prev) => {
      return prev.map((colConversation, colIndex) => {
        if (!enabledPanels[colIndex]) {
          return colConversation; // Don't add messages to minimized panels
        }
        return [
          ...colConversation,
          clarificationMessage,
          {
            id: newAssistantIds[colIndex],
            role: 'assistant' as const,
            content: '',
            isStreaming: true,
          },
        ];
      });
    });

    // Build conversation history for each column (only for enabled panels)
    const buildClarificationMessages = (colIndex: number) => {
      if (!enabledPanels[colIndex]) return [];
      const history = conversations[colIndex]
        .filter((m) => m.content.trim().length > 0)
        .map((m) => ({ role: m.role, content: m.content }));
      return [...history, { role: 'user' as const, content: clarificationPrompt }];
    };

    try {
      const model0 = enabledPanels[0] ? getModelById(columnConfigs[0].modelEntityId) : null;
      const model1 = enabledPanels[1] ? getModelById(columnConfigs[1].modelEntityId) : null;
      const model2 = enabledPanels[2] ? getModelById(columnConfigs[2].modelEntityId) : null;

      const result = await api.startArche('arche_multi_ai', {
        openaiMessages: buildClarificationMessages(0),
        anthropicMessages: buildClarificationMessages(1),
        googleMessages: buildClarificationMessages(2),
        openaiModel: model0?.modelName || '',
        anthropicModel: model1?.modelName || '',
        googleModel: model2?.modelName || '',
        openaiProvider: enabledPanels[0] ? columnConfigs[0].provider : '',
        anthropicProvider: enabledPanels[1] ? columnConfigs[1].provider : '',
        googleProvider: enabledPanels[2] ? columnConfigs[2].provider : '',
      });

      // Subscribe to streaming responses
      const cleanup = subscribeOneOff(result.requestId, {
        onToken: (nodeId, text) => {
          const colIndex = nodeId === 'stream-0' ? 0 : nodeId === 'stream-1' ? 1 : 2;
          setConversations((prev) => {
            const newConversations = [...prev];
            newConversations[colIndex] = prev[colIndex].map((msg) =>
              msg.id === newAssistantIds[colIndex] ? { ...msg, content: msg.content + text } : msg,
            );
            return newConversations;
          });
        },
        onComplete: () => {
          // Mark all as not streaming
          setConversations((prev) =>
            prev.map((colConversation, colIndex) =>
              colConversation.map((msg) =>
                msg.id === newAssistantIds[colIndex] ? { ...msg, isStreaming: false } : msg,
              ),
            ),
          );
          setIsResendingWithClarifications(false);
          setAssumptionClarifications({});
          resendCleanupRef.current = null;
          // Switch to responses tab to show the new responses
          setActiveTab('responses');
        },
        onError: (message) => {
          console.error('Resend streaming error:', message);
          setConversations((prev) =>
            prev.map((colConversation, colIndex) =>
              colConversation.map((msg) =>
                msg.id === newAssistantIds[colIndex]
                  ? { ...msg, content: 'Failed to get response', isStreaming: false }
                  : msg,
              ),
            ),
          );
          setIsResendingWithClarifications(false);
          resendCleanupRef.current = null;
        },
      });
      resendCleanupRef.current = cleanup;
    } catch (err) {
      console.error('Resend with clarifications failed:', err);
      setIsResendingWithClarifications(false);
    }
  };

  // Only check enabled panels for responses
  const hasResponses =
    conversations.every(
      (colConversation, colIndex) =>
        !enabledPanels[colIndex] ||
        colConversation.some((m) => m.role === 'assistant' && m.content.trim()),
    ) && enabledPanelCount > 0;

  // Check if there are any messages at all (for enabling New button)
  const hasAnyMessages = conversations.some((colConversation) => colConversation.length > 0);

  // Check if conversation has changed since last summary was generated
  const currentMessageCount = conversations.reduce((sum, col) => sum + col.length, 0);
  const summaryNeedsUpdate = summaryResult && currentMessageCount > summaryMessageCount;

  // Render a message panel for a column
  const renderPanel = (colIndex: number) => {
    const config = columnConfigs[colIndex];
    const messages = conversations[colIndex];
    const scrollRef = scrollRefs[colIndex];
    const providerModels = modelsByProvider[config.provider] || [];
    const isEnabled = enabledPanels[colIndex];

    // Minimized panel view
    if (!isEnabled) {
      return (
        <div className="flex flex-col h-full w-12 flex-shrink-0 bg-muted/20 border-border">
          <div className="flex-shrink-0 px-1 py-2 border-b border-border bg-muted/30 flex items-center justify-center h-[42px]">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => togglePanel(colIndex)}
              className="h-6 w-6 p-0"
              title={`Enable ${getProviderLabel(config.provider)}`}
            >
              <PanelLeft className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center gap-2">
            <span
              className={cn('text-xs font-medium', getProviderColor(config.provider))}
              style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
            >
              {getProviderLabel(config.provider)}
            </span>
            <span
              className="text-xs text-muted-foreground"
              style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
            >
              (inactive)
            </span>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full min-w-[300px] flex-1">
        <div className="flex-shrink-0 px-3 py-2 border-b border-border bg-muted/30 h-[42px] flex items-center">
          <div className="flex items-center gap-2">
            {/* Minimize button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => togglePanel(colIndex)}
              className="h-6 w-6 p-0"
              title={`Hide ${getProviderLabel(config.provider)} panel`}
              disabled={enabledPanelCount <= 1}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            {/* Provider dropdown */}
            <Select
              value={config.provider}
              onValueChange={(value) =>
                handleColumnProviderChange(colIndex, value as ModelProvider)
              }
              disabled={isStreaming || modelsLoading}
            >
              <SelectTrigger
                className={cn(
                  'h-6 text-xs px-2 w-auto min-w-[80px] font-medium',
                  getProviderColor(config.provider),
                )}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {providers.map((p) => (
                  <SelectItem key={p.key} value={p.key} className="text-xs">
                    <span className={PROVIDER_COLORS[p.key] || ''}>{p.displayName}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Model dropdown */}
            <Select
              value={config.modelEntityId}
              onValueChange={(value) => handleColumnModelChange(colIndex, value)}
              disabled={isStreaming || modelsLoading}
            >
              <SelectTrigger className="h-6 text-xs px-2 w-auto min-w-[100px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {providerModels.map((model) => (
                  <SelectItem key={model.entityId} value={model.entityId} className="text-xs">
                    <div className="flex flex-col gap-0.5">
                      <span>{model.name}</span>
                      <ModelCostLabel pricing={model.pricing} />
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Settings button */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="h-6 w-6 p-0" title="Model settings">
                  <Settings className="h-3.5 w-3.5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] flex flex-col gap-0">
                <SheetHeader>
                  <SheetTitle>{getProviderLabel(config.provider)} Settings</SheetTitle>
                </SheetHeader>
                <SheetBody>
                  <div className="flex flex-col gap-6 mt-6">
                    {/* Show thinking checkbox - common to all models */}
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id={`show-thinking-${colIndex}`}
                        checked={config.showThinking}
                        onCheckedChange={(checked) =>
                          updateColumnConfig(colIndex, { showThinking: checked === true })
                        }
                        disabled={isStreaming}
                      />
                      <Label
                        htmlFor={`show-thinking-${colIndex}`}
                        className="text-sm cursor-pointer"
                      >
                        Show thinking
                      </Label>
                    </div>

                    {/* Model parameters - rendered from Entity schema */}
                    {(() => {
                      const model = getModelById(config.modelEntityId);
                      return (
                        <ColumnParameterPanel
                          provider={config.provider}
                          modelName={model?.modelName}
                          modelDisplayName={model?.name}
                          modelEntityId={model?.entityId}
                          disabled={isStreaming}
                          config={config}
                          onConfigChange={(updates) => updateColumnConfig(colIndex, updates)}
                          allModelParams={allColumnModelParams[colIndex]}
                          onModelParamsChange={(modelId, params) =>
                            updateColumnModelParams(colIndex, modelId, params)
                          }
                        />
                      );
                    })()}
                  </div>
                </SheetBody>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        <div ref={scrollRef.containerRef} className="flex-1 overflow-y-auto p-3">
          {/* Don't show "Waiting for input" while restoring session/conversation */}
          {!sessionInitialized || (conversation?.messages?.length && messages.length === 0) ? (
            <div className="h-full" />
          ) : messages.length === 0 ? (
            <div className="flex items-center justify-center h-full text-center text-muted-foreground text-sm">
              <p>Waiting for input...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={cn(
                    'flex gap-2',
                    message.role === 'user' ? 'justify-end' : 'justify-start',
                  )}
                >
                  {message.role === 'assistant' && (
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                      <Bot className="h-3 w-3 text-primary" />
                    </div>
                  )}
                  <div
                    className={cn(
                      'max-w-[85%] rounded-xl px-3 py-2 text-sm',
                      message.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted',
                    )}
                  >
                    {/* Always show thinking if message has it - toggle only affects new requests */}
                    {message.thinking && (
                      <div className="mb-2 pb-2 border-b border-border/50">
                        <div className="text-xs font-medium text-muted-foreground mb-1">
                          Thinking...
                        </div>
                        <div className="text-xs italic text-muted-foreground whitespace-pre-wrap">
                          {message.thinking}
                        </div>
                      </div>
                    )}
                    <div
                      className={cn(
                        'break-words',
                        message.role === 'assistant' &&
                          'prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-headings:my-2 prose-strong:text-foreground',
                      )}
                    >
                      {message.isStreaming && !message.content && !message.thinking ? (
                        <div className="flex items-center gap-1">
                          <span
                            className="w-1.5 h-1.5 bg-current rounded-full animate-bounce-big"
                            style={{ animationDelay: '0ms' }}
                          />
                          <span
                            className="w-1.5 h-1.5 bg-current rounded-full animate-bounce-big"
                            style={{ animationDelay: '150ms' }}
                          />
                          <span
                            className="w-1.5 h-1.5 bg-current rounded-full animate-bounce-big"
                            style={{ animationDelay: '300ms' }}
                          />
                        </div>
                      ) : message.role === 'assistant' ? (
                        (() => {
                          const {
                            content: displayContent,
                            isError,
                            isInsufficientCredits,
                          } = formatMessageContent(message.content);
                          return (
                            <>
                              {isError ? (
                                <span className="text-amber-600 dark:text-amber-400">
                                  {isInsufficientCredits ? (
                                    <InsufficientCreditsMessage />
                                  ) : (
                                    displayContent
                                  )}
                                </span>
                              ) : (
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                  {displayContent}
                                </ReactMarkdown>
                              )}
                              {message.isStreaming && message.content && (
                                <span className="inline-block w-1.5 h-3 bg-current ml-0.5 animate-pulse" />
                              )}
                            </>
                          );
                        })()
                      ) : (
                        <>
                          {message.content}
                          {message.isStreaming && message.content && (
                            <span className="inline-block w-1.5 h-3 bg-current ml-0.5 animate-pulse" />
                          )}
                        </>
                      )}
                    </div>
                    {/* What this column's response actually cost (shown once finished) */}
                    {message.role === 'assistant' &&
                      !message.isStreaming &&
                      message.costCredits !== undefined && (
                        <div className="mt-1.5 flex justify-end">
                          <InteractionCostLabel costCredits={message.costCredits} />
                        </div>
                      )}
                  </div>
                  {message.role === 'user' && (
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                      <User className="h-3 w-3 text-primary-foreground" />
                    </div>
                  )}
                </div>
              ))}
              <div ref={scrollRef.anchorRef} />
            </div>
          )}
        </div>
      </div>
    );
  };

  // Handle loading a conversation from the sidebar
  const handleLoadConversation = useCallback(
    async (conversationId: string) => {
      await loadConversation(conversationId);
      updateMultiAIPreferences({ activeConversationId: conversationId });
    },
    [loadConversation, updateMultiAIPreferences],
  );

  // Handle updating a conversation title
  const handleUpdateTitle = useCallback(
    async (conversationId: string, title: string) => {
      // If it's the active conversation, update it
      if (conversation?.conversationId === conversationId) {
        await updateTitle(title);
      } else {
        // Otherwise update directly via API
        await api.updateConversation(conversationId, { title });
      }
    },
    [conversation, updateTitle],
  );

  // Handle archiving a conversation
  const handleArchiveConversation = useCallback(
    async (conversationId: string) => {
      if (conversation?.conversationId === conversationId) {
        await archiveConversation();
        setConversations([[], [], []]);
        lastLoadedConversationIdRef.current = null;
        updateMultiAIPreferences({ activeConversationId: null });
      } else {
        await api.updateConversation(conversationId, { status: 'archived' });
      }
      // Always refresh the list to ensure UI is in sync
      await refreshList();
    },
    [conversation, archiveConversation, updateMultiAIPreferences, refreshList],
  );

  // Handle deleting a conversation
  const handleDeleteConversation = useCallback(
    async (conversationId: string) => {
      const isActiveConversation = conversation?.conversationId === conversationId;

      // Use deleteConversationById which updates local state immediately
      await deleteConversationById(conversationId);

      // If we deleted the active conversation, clear all local UI state
      if (isActiveConversation) {
        setConversations([[], [], []]);
        lastLoadedConversationIdRef.current = null;
        pendingUserMessageRef.current = null; // Clear any pending messages
        justCreatedConversationRef.current = false; // Reset the flag
        updateMultiAIPreferences({ activeConversationId: null });
      }
    },
    [conversation, deleteConversationById, updateMultiAIPreferences],
  );

  // Handle exporting a conversation
  const handleExportConversation = useCallback(
    async (conversationId: string, format: 'json' | 'markdown') => {
      try {
        const content = await api.exportConversation(conversationId, format);
        if (content) {
          // Create a download
          const blob = new Blob([content], {
            type: format === 'json' ? 'application/json' : 'text/markdown',
          });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `conversation-${conversationId}.${format === 'json' ? 'json' : 'md'}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
      } catch (err) {
        console.error('Failed to export conversation:', err);
      }
    },
    [],
  );

  // Handle sharing a conversation
  const handleShareConversation = useCallback(async (conversationId: string) => {
    setShareDialogOpen(true);
    setShareLoading(true);
    setShareError(null);
    setShareUrl(null);
    setShareExpiresAt(null);

    try {
      const result = await api.shareConversation(conversationId);
      const fullUrl = `${shareOrigin()}/shared/${result.shareId}`;
      setShareUrl(fullUrl);
      setShareExpiresAt(result.expiresAt);
    } catch (err) {
      console.error('Failed to share conversation:', err);
      setShareError('Failed to create share link. Please try again.');
    } finally {
      setShareLoading(false);
    }
  }, []);

  return (
    <FileContextDropZone onFilesDropped={handleFilesDropped} disabled={isStreaming}>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex-shrink-0 border-b border-border bg-card/50 backdrop-blur-sm">
          <div className="px-4 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={toggle}
                className={`h-11 w-11 flex-shrink-0 bg-gray-100 dark:bg-zinc-700 ${isCollapsed ? '' : 'invisible'}`}
                title="Open sidebar"
              >
                <Menu className="h-6 w-6" />
              </Button>
              <h1 className="text-lg font-semibold">Multi-AI</h1>
              <Link
                to="/marketplace/arches/arche_multi_ai"
                className="text-muted-foreground hover:text-primary transition-colors"
                title="View arche details"
              >
                <Info className="h-6 w-6" />
              </Link>
              <FavoriteButton archeId="arche_multi_ai" />
              <ArcheExpandButton archeId="arche_multi_ai" />
            </div>

            <div className="flex items-center gap-2 md:gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handleReset}
                disabled={isStreaming || !hasAnyMessages}
                className="gap-1.5 [&_svg]:size-5!"
              >
                <SquarePen />
                <span className="hidden sm:inline">New</span>
              </Button>

              {/* Conversation history sidebar */}
              <ConversationSidebar
                conversations={conversationList}
                activeConversationId={conversation?.conversationId || null}
                loading={conversationsLoading}
                onNewConversation={handleReset}
                onLoadConversation={handleLoadConversation}
                onUpdateTitle={handleUpdateTitle}
                onArchiveConversation={handleArchiveConversation}
                onDeleteConversation={handleDeleteConversation}
                onExportConversation={handleExportConversation}
                onShareConversation={handleShareConversation}
                isStreaming={isStreaming}
              />

              {isMobile ? (
                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="outline" size="icon" className="h-9 w-9" title="Settings">
                      <Settings className="h-5 w-5" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-[280px] flex flex-col gap-0">
                    <SheetHeader>
                      <SheetTitle>Settings</SheetTitle>
                    </SheetHeader>
                    <SheetBody>
                      <div className="flex flex-col gap-6 mt-6">
                        {/* Complexity tier selector */}
                        <div className="flex flex-col gap-2">
                          <Label className="text-sm font-medium">Model Complexity</Label>
                          <Select
                            value={selectedTier || 'custom'}
                            onValueChange={(v) =>
                              v !== 'custom' && handleTierChange(v as ModelTier)
                            }
                            disabled={isStreaming}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(TIER_LABELS).map(([tier, label]) => (
                                <SelectItem key={tier} value={tier}>
                                  {label}
                                </SelectItem>
                              ))}
                              {selectedTier === null && (
                                <SelectItem value="custom">Custom</SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">
                            Sets appropriate models for each provider
                          </p>
                        </div>
                        {/* Global Parameters */}
                        <div className="flex flex-col gap-4">
                          <Label className="text-sm font-medium">Global Parameters</Label>
                          {/* Context checkbox */}
                          <div className="flex items-center gap-3">
                            <Checkbox
                              id="include-context-mobile"
                              checked={includeContext}
                              onCheckedChange={(checked) => {
                                const value = checked === true;
                                setIncludeContext(value);
                                updateMultiAIPreferences({ includeContext: value });
                              }}
                              disabled={isStreaming}
                            />
                            <Label
                              htmlFor="include-context-mobile"
                              className="text-sm cursor-pointer"
                            >
                              Include conversation context
                            </Label>
                          </div>
                          {/* Global Show Thinking controls */}
                          <div className="flex flex-col gap-2">
                            <Label className="text-sm text-muted-foreground">Show Thinking</Label>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  // Turn on thinking for all models that support it
                                  setColumnConfigs((prev) => {
                                    const newConfigs = prev.map((config) => ({
                                      ...config,
                                      showThinking: true,
                                    }));
                                    updateMultiAIPreferences({
                                      columnConfigs: newConfigs.map((c) => ({
                                        provider: c.provider,
                                        modelEntityId: c.modelEntityId,
                                        showThinking: c.showThinking,
                                        gpt5Reasoning: c.gpt5Reasoning,
                                        providerSettings: {
                                          gpt4MaxOutputTokens: c.gpt4MaxOutputTokens,
                                          gpt4Temperature: c.gpt4Temperature,
                                          reasoningEffort: c.reasoningEffort,
                                          verbosity: c.verbosity,
                                          gptMaxOutputTokens: c.gptMaxOutputTokens,
                                          claudeMaxTokens: c.claudeMaxTokens,
                                          claudeBudgetTokens: c.claudeBudgetTokens,
                                          claudeTemperature: c.claudeTemperature,
                                          geminiMaxOutputTokens: c.geminiMaxOutputTokens,
                                          geminiTemperature: c.geminiTemperature,
                                          geminiThinkingLevel: c.geminiThinkingLevel,
                                        },
                                      })),
                                    });
                                    return newConfigs;
                                  });
                                  // Also update per-model params for current models
                                  columnConfigs.forEach((config, colIndex) => {
                                    if (config.modelEntityId) {
                                      const currentParams =
                                        allColumnModelParams[colIndex][config.modelEntityId] || {};
                                      updateColumnModelParams(colIndex, config.modelEntityId, {
                                        ...currentParams,
                                        showThinking: true,
                                        thinkingEnabled: true,
                                      });
                                    }
                                  });
                                  toast({ description: 'Show thinking enabled for all models' });
                                }}
                                disabled={isStreaming}
                              >
                                All On
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  // Turn off thinking for all models
                                  setColumnConfigs((prev) => {
                                    const newConfigs = prev.map((config) => ({
                                      ...config,
                                      showThinking: false,
                                    }));
                                    updateMultiAIPreferences({
                                      columnConfigs: newConfigs.map((c) => ({
                                        provider: c.provider,
                                        modelEntityId: c.modelEntityId,
                                        showThinking: c.showThinking,
                                        gpt5Reasoning: c.gpt5Reasoning,
                                        providerSettings: {
                                          gpt4MaxOutputTokens: c.gpt4MaxOutputTokens,
                                          gpt4Temperature: c.gpt4Temperature,
                                          reasoningEffort: c.reasoningEffort,
                                          verbosity: c.verbosity,
                                          gptMaxOutputTokens: c.gptMaxOutputTokens,
                                          claudeMaxTokens: c.claudeMaxTokens,
                                          claudeBudgetTokens: c.claudeBudgetTokens,
                                          claudeTemperature: c.claudeTemperature,
                                          geminiMaxOutputTokens: c.geminiMaxOutputTokens,
                                          geminiTemperature: c.geminiTemperature,
                                          geminiThinkingLevel: c.geminiThinkingLevel,
                                        },
                                      })),
                                    });
                                    return newConfigs;
                                  });
                                  // Also update per-model params for current models
                                  columnConfigs.forEach((config, colIndex) => {
                                    if (config.modelEntityId) {
                                      const currentParams =
                                        allColumnModelParams[colIndex][config.modelEntityId] || {};
                                      updateColumnModelParams(colIndex, config.modelEntityId, {
                                        ...currentParams,
                                        showThinking: false,
                                        thinkingEnabled: false,
                                      });
                                    }
                                  });
                                  toast({ description: 'Show thinking disabled for all models' });
                                }}
                                disabled={isStreaming}
                              >
                                All Off
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </SheetBody>
                  </SheetContent>
                </Sheet>
              ) : (
                <div className="flex items-center gap-4">
                  {/* Global Settings Sheet */}
                  <Sheet>
                    <SheetTrigger asChild>
                      <Button variant="outline" size="sm" className="h-8">
                        <Settings className="h-4 w-4 mr-2" />
                        Settings
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="right" className="w-[280px] flex flex-col gap-0">
                      <SheetHeader>
                        <SheetTitle>Global Settings</SheetTitle>
                      </SheetHeader>
                      <SheetBody>
                        <div className="flex flex-col gap-6 mt-6">
                          {/* Global Parameters */}
                          <div className="flex flex-col gap-4">
                            <Label className="text-sm font-medium">Global Parameters</Label>
                            {/* Context checkbox */}
                            <div className="flex items-center gap-3">
                              <Checkbox
                                id="include-context-desktop"
                                checked={includeContext}
                                onCheckedChange={(checked) => {
                                  const value = checked === true;
                                  setIncludeContext(value);
                                  updateMultiAIPreferences({ includeContext: value });
                                }}
                                disabled={isStreaming}
                              />
                              <Label
                                htmlFor="include-context-desktop"
                                className="text-sm cursor-pointer"
                              >
                                Include conversation context
                              </Label>
                            </div>
                            {/* Global Show Thinking controls */}
                            <div className="flex flex-col gap-2">
                              <Label className="text-sm text-muted-foreground">Show Thinking</Label>
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setColumnConfigs((prev) => {
                                      const newConfigs = prev.map((config) => ({
                                        ...config,
                                        showThinking: true,
                                      }));
                                      updateMultiAIPreferences({
                                        columnConfigs: newConfigs.map((c) => ({
                                          provider: c.provider,
                                          modelEntityId: c.modelEntityId,
                                          showThinking: c.showThinking,
                                          gpt5Reasoning: c.gpt5Reasoning,
                                          providerSettings: {
                                            gpt4MaxOutputTokens: c.gpt4MaxOutputTokens,
                                            gpt4Temperature: c.gpt4Temperature,
                                            reasoningEffort: c.reasoningEffort,
                                            verbosity: c.verbosity,
                                            gptMaxOutputTokens: c.gptMaxOutputTokens,
                                            claudeMaxTokens: c.claudeMaxTokens,
                                            claudeBudgetTokens: c.claudeBudgetTokens,
                                            claudeTemperature: c.claudeTemperature,
                                            geminiMaxOutputTokens: c.geminiMaxOutputTokens,
                                            geminiTemperature: c.geminiTemperature,
                                            geminiThinkingLevel: c.geminiThinkingLevel,
                                          },
                                        })),
                                      });
                                      return newConfigs;
                                    });
                                    columnConfigs.forEach((config, colIndex) => {
                                      if (config.modelEntityId) {
                                        const currentParams =
                                          allColumnModelParams[colIndex][config.modelEntityId] ||
                                          {};
                                        updateColumnModelParams(colIndex, config.modelEntityId, {
                                          ...currentParams,
                                          showThinking: true,
                                          thinkingEnabled: true,
                                        });
                                      }
                                    });
                                    toast({ description: 'Show thinking enabled for all models' });
                                  }}
                                  disabled={isStreaming}
                                >
                                  All On
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setColumnConfigs((prev) => {
                                      const newConfigs = prev.map((config) => ({
                                        ...config,
                                        showThinking: false,
                                      }));
                                      updateMultiAIPreferences({
                                        columnConfigs: newConfigs.map((c) => ({
                                          provider: c.provider,
                                          modelEntityId: c.modelEntityId,
                                          showThinking: c.showThinking,
                                          gpt5Reasoning: c.gpt5Reasoning,
                                          providerSettings: {
                                            gpt4MaxOutputTokens: c.gpt4MaxOutputTokens,
                                            gpt4Temperature: c.gpt4Temperature,
                                            reasoningEffort: c.reasoningEffort,
                                            verbosity: c.verbosity,
                                            gptMaxOutputTokens: c.gptMaxOutputTokens,
                                            claudeMaxTokens: c.claudeMaxTokens,
                                            claudeBudgetTokens: c.claudeBudgetTokens,
                                            claudeTemperature: c.claudeTemperature,
                                            geminiMaxOutputTokens: c.geminiMaxOutputTokens,
                                            geminiTemperature: c.geminiTemperature,
                                            geminiThinkingLevel: c.geminiThinkingLevel,
                                          },
                                        })),
                                      });
                                      return newConfigs;
                                    });
                                    columnConfigs.forEach((config, colIndex) => {
                                      if (config.modelEntityId) {
                                        const currentParams =
                                          allColumnModelParams[colIndex][config.modelEntityId] ||
                                          {};
                                        updateColumnModelParams(colIndex, config.modelEntityId, {
                                          ...currentParams,
                                          showThinking: false,
                                          thinkingEnabled: false,
                                        });
                                      }
                                    });
                                    toast({ description: 'Show thinking disabled for all models' });
                                  }}
                                  disabled={isStreaming}
                                >
                                  All Off
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </SheetBody>
                    </SheetContent>
                  </Sheet>
                  {/* Complexity tier selector */}
                  <Select
                    value={selectedTier || 'custom'}
                    onValueChange={(v) => v !== 'custom' && handleTierChange(v as ModelTier)}
                    disabled={isStreaming}
                  >
                    <SelectTrigger className="w-[120px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(TIER_LABELS).map(([tier, label]) => (
                        <SelectItem key={tier} value={tier}>
                          {label}
                        </SelectItem>
                      ))}
                      {selectedTier === null && <SelectItem value="custom">Custom</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex-shrink-0 border-b border-border bg-muted/30">
          <div className="px-4">
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as ActiveTab)}>
              <TabsList className="h-10 bg-transparent gap-2">
                <TabsTrigger
                  value="responses"
                  className="gap-1.5 data-[state=active]:bg-background data-[state=active]:shadow-sm"
                >
                  <MessageSquare className="h-4 w-4" />
                  <span>Responses</span>
                </TabsTrigger>
                <TabsTrigger
                  value="summary"
                  className="gap-1.5 data-[state=active]:bg-background data-[state=active]:shadow-sm"
                >
                  <Sparkles className="h-4 w-4" />
                  <span>Summary</span>
                  {summaryResult && <span className="ml-1 w-2 h-2 bg-primary rounded-full" />}
                </TabsTrigger>
                <TabsTrigger
                  value="assumptions"
                  className="gap-1.5 data-[state=active]:bg-background data-[state=active]:shadow-sm"
                >
                  <ListChecks className="h-4 w-4" />
                  <span>Assumptions</span>
                  {assumptionsResult && <span className="ml-1 w-2 h-2 bg-primary rounded-full" />}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>

        {/* Tab content */}
        {activeTab === 'responses' && (
          <>
            {/* Three-panel layout */}
            <div ref={panelsContainerRef} className="flex-1 overflow-x-auto scrollbar-hide">
              <div
                ref={messagesContainerRef}
                className="h-full flex flex-row divide-x divide-border"
              >
                {/* Learn More popup for text selection */}
                {highlight.isVisible && highlight.position && (
                  <LearnMorePopup
                    position={highlight.position}
                    onClick={highlight.handleLearnMore}
                  />
                )}
                {renderPanel(0)}
                {renderPanel(1)}
                {renderPanel(2)}
              </div>
            </div>

            {/* Custom horizontal scrollbar - only show when content overflows */}
            {scrollState.scrollWidth > scrollState.clientWidth && (
              <div className="flex-shrink-0 border-t border-border bg-muted/30 px-12 py-3">
                <div
                  ref={scrollTrackRef}
                  className="relative h-6 cursor-pointer flex items-center"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleScrollbarDrag(e.clientX);

                    const handleMouseMove = (moveEvent: MouseEvent) => {
                      handleScrollbarDrag(moveEvent.clientX);
                    };
                    const handleMouseUp = () => {
                      document.removeEventListener('mousemove', handleMouseMove);
                      document.removeEventListener('mouseup', handleMouseUp);
                    };
                    document.addEventListener('mousemove', handleMouseMove);
                    document.addEventListener('mouseup', handleMouseUp);
                  }}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    const touch = e.touches[0];
                    handleScrollbarDrag(touch.clientX);

                    const handleTouchMove = (moveEvent: TouchEvent) => {
                      const moveTouch = moveEvent.touches[0];
                      handleScrollbarDrag(moveTouch.clientX);
                    };
                    const handleTouchEnd = () => {
                      document.removeEventListener('touchmove', handleTouchMove);
                      document.removeEventListener('touchend', handleTouchEnd);
                    };
                    document.addEventListener('touchmove', handleTouchMove, { passive: false });
                    document.addEventListener('touchend', handleTouchEnd);
                  }}
                >
                  {/* Thin track line */}
                  <div className="absolute inset-x-0 h-1 bg-muted rounded-full" />
                  {/* Circular Thumb */}
                  <div
                    className="absolute w-6 h-6 bg-primary/80 hover:bg-primary rounded-full transition-colors shadow-sm"
                    style={{
                      left: `calc(${(scrollState.scrollLeft / (scrollState.scrollWidth - scrollState.clientWidth)) * 100}% - ${(scrollState.scrollLeft / (scrollState.scrollWidth - scrollState.clientWidth)) * 24}px)`,
                      top: '50%',
                      transform: 'translateY(-50%)',
                    }}
                  />
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'summary' && (
          <>
            <div ref={summaryContainerRef} className="flex-1 overflow-y-auto bg-background">
              <div className="max-w-4xl mx-auto px-4 py-6">
                {!summaryResult && !isSummarizing ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Sparkles className="h-12 w-12 text-muted-foreground/50 mb-4" />
                    <h3 className="text-lg font-medium mb-2">No Summary Yet</h3>
                    <p className="text-muted-foreground mb-6 max-w-md">
                      {hasResponses
                        ? 'Generate a comparative summary of all three AI responses to see their key similarities and differences.'
                        : 'After you have messaged AIs in the Responses tab, you can create a summary here.'}
                    </p>
                    {/* Provider and Model selection */}
                    <div className="flex items-center gap-2 mb-4">
                      <Select
                        value={summaryProvider}
                        onValueChange={(value) => {
                          setSummaryProvider(value as ModelProvider);
                          // Reset model when provider changes
                          const newProviderModels = modelsByProvider[value as ModelProvider] || [];
                          const recommended = getRecommendedModel(value as ModelProvider);
                          setSummaryModelId(
                            recommended?.entityId || newProviderModels[0]?.entityId || null,
                          );
                        }}
                        disabled={isSummarizing || isStreaming}
                      >
                        <SelectTrigger className="w-[120px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {providers.map((p) => (
                            <SelectItem key={p.key} value={p.key}>
                              {p.displayName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={summaryModelId || ''}
                        onValueChange={setSummaryModelId}
                        disabled={isSummarizing || isStreaming}
                      >
                        <SelectTrigger className="w-[180px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(modelsByProvider[summaryProvider] || []).map((model) => (
                            <SelectItem key={model.entityId} value={model.entityId}>
                              <div className="flex flex-col gap-0.5">
                                <span>{model.name}</span>
                                <ModelCostLabel pricing={model.pricing} />
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      onClick={handleSummarize}
                      disabled={!hasResponses || isSummarizing || isStreaming}
                      className="gap-2"
                    >
                      <Sparkles className="h-4 w-4" />
                      Generate Summary
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Summary header and content */}
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-medium flex items-center gap-2">
                          <Sparkles className="h-5 w-5 text-primary" />
                          Comparative Summary
                          {summaryNeedsUpdate && (
                            <span className="text-xs font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded">
                              New messages available
                            </span>
                          )}
                        </h3>
                        <div className="flex items-center gap-2">
                          {/* Provider and Model selection */}
                          <Select
                            value={summaryProvider}
                            onValueChange={(value) => {
                              setSummaryProvider(value as ModelProvider);
                              const newProviderModels =
                                modelsByProvider[value as ModelProvider] || [];
                              const recommended = getRecommendedModel(value as ModelProvider);
                              setSummaryModelId(
                                recommended?.entityId || newProviderModels[0]?.entityId || null,
                              );
                            }}
                            disabled={isSummarizing || isStreaming}
                          >
                            <SelectTrigger className="h-8 w-[100px] text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {providers.map((p) => (
                                <SelectItem key={p.key} value={p.key} className="text-xs">
                                  {p.displayName}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select
                            value={summaryModelId || ''}
                            onValueChange={setSummaryModelId}
                            disabled={isSummarizing || isStreaming}
                          >
                            <SelectTrigger className="h-8 w-[140px] text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {(modelsByProvider[summaryProvider] || []).map((model) => (
                                <SelectItem
                                  key={model.entityId}
                                  value={model.entityId}
                                  className="text-xs"
                                >
                                  <div className="flex flex-col gap-0.5">
                                    <span>{model.name}</span>
                                    <ModelCostLabel pricing={model.pricing} />
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {isSummarizing ? (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={handleStopSummary}
                              className="gap-1.5"
                            >
                              <Square className="h-4 w-4" />
                              Stop
                            </Button>
                          ) : (
                            <Button
                              variant={summaryNeedsUpdate ? 'default' : 'outline'}
                              size="sm"
                              onClick={handleSummarize}
                              disabled={isStreaming}
                              className="gap-1.5"
                            >
                              <Sparkles className="h-4 w-4" />
                              {summaryNeedsUpdate ? 'Update' : 'Regenerate'}
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="prose prose-sm dark:prose-invert max-w-none prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-headings:my-3 prose-table:w-full prose-th:border prose-th:border-border prose-th:px-3 prose-th:py-2 prose-th:bg-muted prose-td:border prose-td:border-border prose-td:px-3 prose-td:py-2">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {summaryResult || ''}
                        </ReactMarkdown>
                        {isSummarizing && (
                          <span className="inline-block w-1.5 h-4 bg-primary ml-1 animate-pulse" />
                        )}
                      </div>
                    </div>

                    {/* Follow-up conversation about the summary */}
                    {summaryConversation.length > 0 && (
                      <div className="border-t border-border pt-6">
                        <h4 className="text-sm font-medium text-muted-foreground mb-4">
                          Follow-up Discussion
                        </h4>
                        <div className="space-y-4">
                          {summaryConversation.map((message) => (
                            <div
                              key={message.id}
                              className={cn(
                                'flex gap-3',
                                message.role === 'user' ? 'justify-end' : 'justify-start',
                              )}
                            >
                              {message.role === 'assistant' && (
                                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                  <Bot className="h-4 w-4 text-primary" />
                                </div>
                              )}
                              <div
                                className={cn(
                                  'max-w-[80%] rounded-xl px-4 py-3 text-sm',
                                  message.role === 'user'
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-muted',
                                )}
                              >
                                {message.isStreaming && !message.content ? (
                                  <div className="flex items-center gap-1">
                                    <span
                                      className="w-1.5 h-1.5 bg-current rounded-full animate-bounce"
                                      style={{ animationDelay: '0ms' }}
                                    />
                                    <span
                                      className="w-1.5 h-1.5 bg-current rounded-full animate-bounce"
                                      style={{ animationDelay: '150ms' }}
                                    />
                                    <span
                                      className="w-1.5 h-1.5 bg-current rounded-full animate-bounce"
                                      style={{ animationDelay: '300ms' }}
                                    />
                                  </div>
                                ) : message.role === 'assistant' ? (
                                  <div className="prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-table:w-full prose-th:border prose-th:border-border prose-th:px-2 prose-th:py-1 prose-th:bg-muted/50 prose-td:border prose-td:border-border prose-td:px-2 prose-td:py-1">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                      {message.content}
                                    </ReactMarkdown>
                                    {message.isStreaming && message.content && (
                                      <span className="inline-block w-1.5 h-4 bg-current ml-0.5 animate-pulse" />
                                    )}
                                  </div>
                                ) : (
                                  <div className="whitespace-pre-wrap break-words">
                                    {message.content}
                                  </div>
                                )}
                              </div>
                              {message.role === 'user' && (
                                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                                  <User className="h-4 w-4 text-primary-foreground" />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {/* Scroll anchor for auto-scroll */}
                <div ref={summaryAnchorRef} />
              </div>
            </div>

            {/* Input area for summary questions */}
            {summaryResult && !isSummarizing && (
              <div className="flex-shrink-0 border-t border-border bg-background">
                <div className="max-w-4xl mx-auto px-4 py-4">
                  <form onSubmit={handleSummaryQuestion} className="relative">
                    <Textarea
                      ref={summaryTextareaRef}
                      value={summaryInputValue}
                      onChange={(e) => setSummaryInputValue(e.target.value)}
                      onKeyDown={handleSummaryKeyDown}
                      placeholder="Ask a question about this summary..."
                      disabled={isSummaryStreaming}
                      className="min-h-[44px] max-h-[200px] pr-14 resize-none rounded-2xl bg-muted/50 overflow-y-auto"
                      rows={1}
                    />
                    {isSummaryStreaming ? (
                      <Button
                        type="button"
                        size="icon"
                        variant="destructive"
                        onClick={handleStopSummary}
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-xl"
                      >
                        <Square className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button
                        type="submit"
                        size="icon"
                        disabled={!summaryInputValue.trim()}
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-xl"
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    )}
                  </form>
                  <p className="text-xs text-muted-foreground text-center mt-2">
                    Ask follow-up questions about the summary (powered by GPT-5.2)
                  </p>
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'assumptions' && (
          <div className="flex-1 overflow-y-auto bg-background">
            {!assumptionsResult && !isCheckingAssumptions ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <ListChecks className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium mb-2">No Assumptions Checked</h3>
                <p className="text-muted-foreground mb-6 max-w-md">
                  {hasResponses
                    ? 'Ask each AI to identify the assumptions they made when formulating their responses.'
                    : 'After you have messaged AIs in the Responses tab, you can check their assumptions here.'}
                </p>
                {/* Consolidation provider/model selection */}
                <p className="text-xs text-muted-foreground mb-2">Consolidation AI:</p>
                <div className="flex items-center gap-2 mb-4">
                  <Select
                    value={consolidationProvider}
                    onValueChange={(value) => {
                      setConsolidationProvider(value as ModelProvider);
                      const newProviderModels = modelsByProvider[value as ModelProvider] || [];
                      const recommended = getRecommendedModel(value as ModelProvider);
                      setConsolidationModelId(
                        recommended?.entityId || newProviderModels[0]?.entityId || null,
                      );
                    }}
                    disabled={isCheckingAssumptions || isStreaming}
                  >
                    <SelectTrigger className="w-[120px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {providers.map((p) => (
                        <SelectItem key={p.key} value={p.key}>
                          {p.displayName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={consolidationModelId || ''}
                    onValueChange={setConsolidationModelId}
                    disabled={isCheckingAssumptions || isStreaming}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(modelsByProvider[consolidationProvider] || []).map((model) => (
                        <SelectItem key={model.entityId} value={model.entityId}>
                          <div className="flex flex-col gap-0.5">
                            <span>{model.name}</span>
                            <ModelCostLabel pricing={model.pricing} />
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={handleCheckAssumptions}
                  disabled={!hasResponses || isCheckingAssumptions || isStreaming}
                  className="gap-2"
                >
                  <ListChecks className="h-4 w-4" />
                  Check Assumptions
                </Button>
              </div>
            ) : (
              <div className="flex flex-col h-full">
                {/* Header with recheck button */}
                <div className="flex-shrink-0 px-4 py-3 border-b border-border bg-muted/30">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium flex items-center gap-2">
                      <ListChecks className="h-5 w-5 text-primary" />
                      Assumptions Analysis
                      {(isCheckingAssumptions || isConsolidating) && (
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      )}
                    </h3>
                    {isCheckingAssumptions || isConsolidating ? (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleStopAssumptions}
                        className="gap-1.5"
                      >
                        <Square className="h-4 w-4" />
                        Stop
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCheckAssumptions}
                        disabled={isStreaming}
                        className="gap-1.5"
                      >
                        <ListChecks className="h-4 w-4" />
                        Recheck
                      </Button>
                    )}
                  </div>
                </div>

                {/* Three-column raw assumptions */}
                <div
                  ref={assumptionsPanelsRef}
                  className="flex-shrink-0 border-b border-border overflow-x-auto scrollbar-hide"
                >
                  <div className="flex flex-row divide-x divide-border">
                    {[0, 1, 2].map((colIndex) => {
                      const config = columnConfigs[colIndex];
                      const model = getModelById(config.modelEntityId);
                      return (
                        <div key={colIndex} className="flex-1 min-w-[300px]">
                          <div className="px-3 py-2 border-b border-border bg-muted/30">
                            <span
                              className={cn(
                                'font-medium text-sm',
                                getProviderColor(config.provider),
                              )}
                            >
                              {getProviderLabel(config.provider)}
                            </span>
                            <span className="text-xs text-muted-foreground ml-2">
                              {model?.name || 'Unknown'}
                            </span>
                          </div>
                          <div className="p-3 max-h-[250px] overflow-y-auto">
                            {assumptionsResult?.[colIndex] ? (
                              <p className="text-sm whitespace-pre-wrap text-muted-foreground">
                                {assumptionsResult[colIndex]}
                                {isCheckingAssumptions && (
                                  <span className="inline-block w-1.5 h-4 bg-primary ml-1 animate-pulse" />
                                )}
                              </p>
                            ) : isCheckingAssumptions ? (
                              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span>Gathering...</span>
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground italic">No assumptions</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Custom horizontal scrollbar for assumptions - only show when content overflows */}
                {assumptionsScrollState.scrollWidth > assumptionsScrollState.clientWidth && (
                  <div className="flex-shrink-0 border-b border-border bg-muted/30 px-4 py-3">
                    <div
                      ref={assumptionsScrollTrackRef}
                      className="relative h-6 cursor-pointer flex items-center"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleAssumptionsScrollbarDrag(e.clientX);

                        const handleMouseMove = (moveEvent: MouseEvent) => {
                          handleAssumptionsScrollbarDrag(moveEvent.clientX);
                        };
                        const handleMouseUp = () => {
                          document.removeEventListener('mousemove', handleMouseMove);
                          document.removeEventListener('mouseup', handleMouseUp);
                        };
                        document.addEventListener('mousemove', handleMouseMove);
                        document.addEventListener('mouseup', handleMouseUp);
                      }}
                      onTouchStart={(e) => {
                        e.preventDefault();
                        const touch = e.touches[0];
                        handleAssumptionsScrollbarDrag(touch.clientX);

                        const handleTouchMove = (moveEvent: TouchEvent) => {
                          const moveTouch = moveEvent.touches[0];
                          handleAssumptionsScrollbarDrag(moveTouch.clientX);
                        };
                        const handleTouchEnd = () => {
                          document.removeEventListener('touchmove', handleTouchMove);
                          document.removeEventListener('touchend', handleTouchEnd);
                        };
                        document.addEventListener('touchmove', handleTouchMove, { passive: false });
                        document.addEventListener('touchend', handleTouchEnd);
                      }}
                    >
                      {/* Thin track line */}
                      <div className="absolute inset-x-0 h-1 bg-muted rounded-full" />
                      {/* Circular Thumb */}
                      <div
                        className="absolute w-6 h-6 bg-primary/80 hover:bg-primary rounded-full transition-colors shadow-sm"
                        style={{
                          left: `calc(${(assumptionsScrollState.scrollLeft / (assumptionsScrollState.scrollWidth - assumptionsScrollState.clientWidth)) * 100}% - ${(assumptionsScrollState.scrollLeft / (assumptionsScrollState.scrollWidth - assumptionsScrollState.clientWidth)) * 24}px)`,
                          top: '50%',
                          transform: 'translateY(-50%)',
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Consolidated table section */}
                <div className="flex-1 overflow-y-auto">
                  <div className="px-4 py-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                        Consolidated View
                        {isConsolidating && <Loader2 className="h-3 w-3 animate-spin" />}
                      </h4>
                      {/* Consolidation provider/model selection */}
                      <div className="flex items-center gap-2">
                        <Select
                          value={consolidationProvider}
                          onValueChange={(value) => {
                            setConsolidationProvider(value as ModelProvider);
                            const newProviderModels =
                              modelsByProvider[value as ModelProvider] || [];
                            const recommended = getRecommendedModel(value as ModelProvider);
                            setConsolidationModelId(
                              recommended?.entityId || newProviderModels[0]?.entityId || null,
                            );
                          }}
                          disabled={isConsolidating || isCheckingAssumptions}
                        >
                          <SelectTrigger className="h-7 w-[100px] text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {providers.map((p) => (
                              <SelectItem key={p.key} value={p.key} className="text-xs">
                                {p.displayName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          value={consolidationModelId || ''}
                          onValueChange={setConsolidationModelId}
                          disabled={isConsolidating || isCheckingAssumptions}
                        >
                          <SelectTrigger className="h-7 w-[140px] text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {(modelsByProvider[consolidationProvider] || []).map((model) => (
                              <SelectItem
                                key={model.entityId}
                                value={model.entityId}
                                className="text-xs"
                              >
                                <div className="flex flex-col gap-0.5">
                                  <span>{model.name}</span>
                                  <ModelCostLabel pricing={model.pricing} />
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {consolidatedAssumptions && consolidatedAssumptions.length > 0 ? (
                      <>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Assumption</TableHead>
                              {enabledPanels[0] && (
                                <TableHead className="w-24 text-center">
                                  <span className={getProviderColor(columnConfigs[0].provider)}>
                                    {getProviderLabel(columnConfigs[0].provider)}
                                  </span>
                                </TableHead>
                              )}
                              {enabledPanels[1] && (
                                <TableHead className="w-24 text-center">
                                  <span className={getProviderColor(columnConfigs[1].provider)}>
                                    {getProviderLabel(columnConfigs[1].provider)}
                                  </span>
                                </TableHead>
                              )}
                              {enabledPanels[2] && (
                                <TableHead className="w-24 text-center">
                                  <span className={getProviderColor(columnConfigs[2].provider)}>
                                    {getProviderLabel(columnConfigs[2].provider)}
                                  </span>
                                </TableHead>
                              )}
                              <TableHead className="w-24 text-center">Clarify</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {consolidatedAssumptions.map((item, index) => (
                              <React.Fragment key={index}>
                                <TableRow>
                                  <TableCell className="text-sm">
                                    <div>{item.assumption}</div>
                                    {assumptionClarifications[index] &&
                                      editingAssumptionIndex !== index && (
                                        <div className="mt-1 text-xs text-primary bg-primary/10 px-2 py-1 rounded">
                                          Clarification: {assumptionClarifications[index]}
                                        </div>
                                      )}
                                  </TableCell>
                                  {enabledPanels[0] && (
                                    <TableCell className="text-center">
                                      {item.col0 && (
                                        <Check
                                          className={cn(
                                            'h-5 w-5 mx-auto',
                                            getProviderColor(columnConfigs[0].provider),
                                          )}
                                        />
                                      )}
                                    </TableCell>
                                  )}
                                  {enabledPanels[1] && (
                                    <TableCell className="text-center">
                                      {item.col1 && (
                                        <Check
                                          className={cn(
                                            'h-5 w-5 mx-auto',
                                            getProviderColor(columnConfigs[1].provider),
                                          )}
                                        />
                                      )}
                                    </TableCell>
                                  )}
                                  {enabledPanels[2] && (
                                    <TableCell className="text-center">
                                      {item.col2 && (
                                        <Check
                                          className={cn(
                                            'h-5 w-5 mx-auto',
                                            getProviderColor(columnConfigs[2].provider),
                                          )}
                                        />
                                      )}
                                    </TableCell>
                                  )}
                                  <TableCell className="text-center">
                                    {editingAssumptionIndex === index ? (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setEditingAssumptionIndex(null)}
                                        className="h-8 w-8 p-0"
                                        title="Done editing"
                                      >
                                        <Check className="h-4 w-4" />
                                      </Button>
                                    ) : (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setEditingAssumptionIndex(index)}
                                        disabled={isResendingWithClarifications}
                                        className="h-8 w-8 p-0"
                                        title="Clarify this assumption"
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </TableCell>
                                </TableRow>
                                {editingAssumptionIndex === index && (
                                  <TableRow>
                                    <TableCell
                                      colSpan={2 + enabledPanelCount}
                                      className="pt-0 pb-3"
                                    >
                                      <div className="flex gap-2 items-start">
                                        <Textarea
                                          value={assumptionClarifications[index] || ''}
                                          onChange={(e) =>
                                            handleClarificationChange(index, e.target.value)
                                          }
                                          onKeyDown={(e) => {
                                            // Enter without Shift exits editing mode
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                              e.preventDefault();
                                              setEditingAssumptionIndex(null);
                                            }
                                            // Shift+Enter allows normal new line behavior
                                          }}
                                          placeholder="Enter your clarification for this assumption... (Enter to save, Shift+Enter for new line)"
                                          className="flex-1 min-h-[60px] text-sm"
                                          autoFocus
                                        />
                                        {assumptionClarifications[index] && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleRemoveClarification(index)}
                                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                            title="Remove clarification"
                                          >
                                            <X className="h-4 w-4" />
                                          </Button>
                                        )}
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            ))}
                          </TableBody>
                        </Table>

                        {/* Resend with clarifications button */}
                        {hasClarifications && (
                          <div className="mt-4 flex justify-end">
                            <Button
                              onClick={handleResendWithClarifications}
                              disabled={isResendingWithClarifications || isStreaming}
                              className="gap-2"
                            >
                              {isResendingWithClarifications ? (
                                <>
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  Resending...
                                </>
                              ) : (
                                <>
                                  <RotateCcw className="h-4 w-4" />
                                  Resend with Clarifications
                                </>
                              )}
                            </Button>
                          </div>
                        )}
                      </>
                    ) : isConsolidating ? (
                      <div className="border rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Loader2 className="h-4 w-4 animate-spin text-primary" />
                          <span className="text-sm text-muted-foreground">
                            Consolidating assumptions...
                          </span>
                        </div>
                        {consolidationStreamText && (
                          <pre className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-md overflow-x-auto max-h-[300px] overflow-y-auto whitespace-pre-wrap font-mono">
                            {consolidationStreamText}
                            <span className="inline-block w-1.5 h-4 bg-primary ml-0.5 animate-pulse" />
                          </pre>
                        )}
                      </div>
                    ) : isCheckingAssumptions ? (
                      <div className="border rounded-lg p-6 text-center">
                        <p className="text-sm text-muted-foreground">
                          Waiting for all providers to complete...
                        </p>
                      </div>
                    ) : (
                      <div className="border rounded-lg p-6 text-center">
                        <p className="text-sm text-muted-foreground">
                          Consolidation not available. View raw assumptions above.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Input area - only show on responses tab */}
        {activeTab === 'responses' && (
          <div className="flex-shrink-0 border-t border-border bg-background">
            <div className="max-w-4xl mx-auto px-4 py-4">
              {/* Attached files display */}
              {attachedFiles.length > 0 && (
                <AttachedFiles
                  files={attachedFiles}
                  totalSize={attachedFilesSize}
                  onRemove={removeAttachedFile}
                  onClearAll={clearAttachedFiles}
                  className="mb-3"
                />
              )}
              <form onSubmit={handleSubmit} className="relative">
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={() => setFilePickerOpen(true)}
                  disabled={isStreaming || enabledPanelCount === 0 || isProcessingFiles}
                  className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-lg text-muted-foreground hover:text-foreground"
                  title="Attach file from storage"
                >
                  <Paperclip className="h-4 w-4" />
                </Button>
                <Textarea
                  ref={textareaRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    attachedFiles.length > 0
                      ? 'Ask about the attached files...'
                      : `Ask ${enabledPanelCount === 1 ? 'the AI' : enabledPanelCount === 2 ? 'both AIs' : 'all three AIs'} or drop files...`
                  }
                  disabled={isStreaming || enabledPanelCount === 0 || isProcessingFiles}
                  className="min-h-[44px] max-h-[200px] pl-12 pr-14 resize-none rounded-2xl bg-muted/50 overflow-y-auto"
                  rows={1}
                  style={{ height: 'auto' }}
                />
                {isStreaming ? (
                  <Button
                    type="button"
                    size="icon"
                    variant="destructive"
                    onClick={handleStop}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-xl"
                  >
                    <Square className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    size="icon"
                    disabled={!inputValue.trim()}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-xl"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                )}
              </form>
              <p className="text-xs text-muted-foreground text-center mt-2">
                Press Enter to send
                {enabledPanelCount < 3
                  ? ` to ${enabledPanelCount} AI${enabledPanelCount !== 1 ? 's' : ''}`
                  : ' to all three AIs'}
                , Shift+Enter for new line
              </p>
            </div>
          </div>
        )}

        {/* Share Dialog */}
        <ShareDialog
          open={shareDialogOpen}
          onOpenChange={setShareDialogOpen}
          shareUrl={shareUrl}
          expiresAt={shareExpiresAt}
          loading={shareLoading}
          error={shareError}
        />

        {/* File Picker Dialog — Vault-backed; extracts text for AI context */}
        <VaultFilePickerDialog
          open={filePickerOpen}
          onOpenChange={setFilePickerOpen}
          title="Select File as Context"
          description="Choose a file from your storage to add as context for the AI. Supported: text, .docx, .pdf."
          mimeFilter={(doc) => isVaultDocumentExtractable(doc.mimeType, doc.name)}
          confirmLabel="Add as Context"
          onFileSelected={async (picked) => {
            if (!isVaultDocumentExtractable(picked.mimeType, picked.fileName)) {
              toast({
                title: 'Unsupported file type',
                description: 'Only text files, .docx, and .pdf are supported.',
                variant: 'destructive',
              });
              return;
            }
            if (picked.sizeBytes > MAX_FILE_SIZE) {
              toast({
                title: 'File too large',
                description: `File exceeds maximum size of ${formatBytes(MAX_FILE_SIZE)}`,
                variant: 'destructive',
              });
              return;
            }
            try {
              const extracted = await extractTextFromVaultDocument(picked);
              addFileWithContent(extracted);
              toast({ description: `Added "${extracted.name}" as context` });
            } catch (err) {
              toast({
                title:
                  err instanceof VaultDocumentExtractionError
                    ? 'Failed to load file'
                    : 'Unexpected error',
                description: err instanceof Error ? err.message : 'An error occurred',
                variant: 'destructive',
              });
            }
          }}
        />
      </div>
    </FileContextDropZone>
  );
}

export function MultiAIPage() {
  return (
    <ArcheLayout>
      <MultiAIContent />
    </ArcheLayout>
  );
}

export default MultiAIPage;
